import React, { useEffect, useRef, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, useParams } from 'react-router-dom';
import './App.css';
import { LangProvider, useLang, LANGS, LANG_LABELS } from './LangContext';
import { L as Link } from './L';
import { useSiteContent } from './SiteContentContext';
import { SEO } from './SEO';
import CookieBanner from './CookieBanner';
import AIChatWidget from './AIChatWidget';
import { MODULES } from './data/modules';
import { lazyWithRetry as lazy } from './lazyWithRetry';

/* Module entries shown in the navbar mega-menu (ordered list).
   Accent colors mirror the tier-based palette of /moduli:
   Core = cyan (#00bcd4) · Premium = purple (#a78bfa). */
const MODULI_MENU = [
  { slug: 'listing-catalogo',    icon: 'bi-list-check', accent: '#00bcd4', fallbackTitle: 'Listing & Catalogo' },
  { slug: 'inventario-prezzi',   icon: 'bi-boxes',      accent: '#00bcd4', fallbackTitle: 'Inventario, stock e prezzi' },
  { slug: 'amministrazione',     icon: 'bi-calculator', accent: '#00bcd4', fallbackTitle: 'Amministrazione e contabilità' },
  { slug: 'competitor-tracking', icon: 'bi-eye',        accent: '#a78bfa', fallbackTitle: 'Competitor tracking' },
  { slug: 'logistica',           icon: 'bi-truck',      accent: '#a78bfa', fallbackTitle: 'Logistica intelligente' },
  { slug: 'analytics',           icon: 'bi-bar-chart',  accent: '#a78bfa', fallbackTitle: 'BI & Analytics' },
  { slug: 'marketing-advertising', icon: 'bi-megaphone', accent: '#f59e0b', fallbackTitle: 'Marketing & Advertising', fallbackDesc: 'Campagne Amazon Ads, Amazon DSP e marketplace con AI', preview: true },
  { slug: 'campaign-manager',    icon: 'bi-bullseye',   accent: '#f59e0b', fallbackTitle: 'Campaign Manager', fallbackDesc: 'Crea, pianifica e monitora tutte le campagne', preview: true },
];

/* Integrations mega-menu categories.
   Each category shows 3 visual icons/logos (either bootstrap icons or
   real logo files) + label + subtitle. Click on the category label goes
   to /integrazioni?cat=<id>. */
const INTEGRAZIONI_MENU = [
  {
    id: 'marketplace',
    icon: 'bi-shop',
    label: 'Marketplace',
    desc: 'Amazon, eBay, Temu, TikTok Shop, ManoMano',
    logos: [
      '/frontend_assets/media/images/marketplaces/amazon-logo.svg',
      '/frontend_assets/media/images/marketplaces/ebay-logo.svg',
      '/frontend_assets/media/images/marketplaces/temu-logo.svg',
    ],
  },
  {
    id: 'cms',
    icon: 'bi-cart3',
    label: 'E-commerce',
    desc: 'Shopify, WooCommerce, PrestaShop, Magento',
    logos: [
      '/frontend_assets/media/images/marketplaces/Shopify_logo.svg',
      '/frontend_assets/media/images/marketplaces/magento.svg',
      '/frontend_assets/media/images/marketplaces/erp.webp',
    ],
  },
  {
    id: 'corrieri',
    icon: 'bi-truck',
    label: 'Corrieri',
    desc: 'BRT, DHL, TNT, Poste Italiane, Mail Boxes Etc.',
    logoIcons: ['bi-truck-flatbed', 'bi-truck-front', 'bi-truck'],
  },
  {
    id: 'erp',
    icon: 'bi-diagram-3',
    label: 'Gestionali ERP',
    desc: 'SAP, Zucchetti, Sistemi, TeamSystem, Dynamics 365',
    logos: [
      '/frontend_assets/media/images/marketplaces/sap_logo.svg',
      '/frontend_assets/media/images/marketplaces/zucchetti.webp',
      '/frontend_assets/media/images/marketplaces/dynamics_365.svg',
    ],
  },
  {
    id: 'fatturazione',
    icon: 'bi-receipt',
    label: 'Fatturazione',
    desc: 'Fatture in Cloud, TeamSystem, SDI XML, IVA OSS',
    logos: [
      '/frontend_assets/media/images/marketplaces/teamSystem_logo.webp',
    ],
    logoIcons: ['bi-cloud-check', 'bi-file-earmark-text'],
  },
  {
    id: 'fornitori',
    icon: 'bi-people',
    label: 'Fornitori',
    desc: 'Collega i tuoi fornitori tramite API o file Excel / CSV',
    logoIcons: ['bi-cloud-arrow-up', 'bi-file-earmark-excel', 'bi-arrow-left-right'],
  },
  {
    id: 'ai-mcp',
    icon: 'bi-robot',
    label: 'AI MCP',
    desc: 'Claude, ChatGPT, Copilot, Gemini — chiedi in chat i dati del tuo gestionale',
    logos: [
      '/frontend_assets/media/images/marketplaces/claude-logo.webp',
      '/frontend_assets/media/images/marketplaces/openai-logo.svg',
      '/frontend_assets/media/images/marketplaces/copilot-logo.svg',
    ],
  },
  {
    id: 'logistica',
    icon: 'bi-box-seam',
    label: 'Logistica',
    desc: 'Amazon FBA, Huboo, Hyve, 3PL, magazzino interno',
    logos: [
      '/frontend_assets/media/images/marketplaces/amazon-fba-logo.webp',
    ],
    logoIcons: ['bi-box-seam', 'bi-building'],
  },
];

// Lazy-loaded pages: only loaded when user navigates to the route. Reduces main bundle / TBT.
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const PrezziPage = lazy(() => import('./pages/Prezzi'));
const RisorsePage = lazy(() => import('./pages/RisorsePage'));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));
const IntegrazioniPage = lazy(() => import('./pages/Integrazioni'));
const SupportoPage = lazy(() => import('./pages/Supporto'));
const ContattiPage = lazy(() => import('./pages/Contatti'));
const BlogPage = lazy(() => import('./pages/Blog'));
const MarketplaceDetail = lazy(() => import('./pages/MarketplaceDetail'));
const ModuliPage = lazy(() => import('./pages/Moduli'));
const FaqFullPage = lazy(() => import('./pages/FaqFull'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const ClientiPage = lazy(() => import('./pages/Clienti'));
const ClienteDetailPage = lazy(() => import('./pages/ClienteDetail'));
const TermsPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPage })));
const ModuleDetail = lazy(() => import('./pages/ModuleDetail'));
const AmazonDSPPage = lazy(() => import('./pages/AmazonDSP'));
const McpConnectorDocsPage = lazy(() => import('./pages/McpConnectorPages').then(m => ({ default: m.McpConnectorDocsPage })));
const SecurityPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.SecurityPage })));
const McpConnectorPrivacyPage = lazy(() => import('./pages/McpConnectorPages').then(m => ({ default: m.McpConnectorPrivacyPage })));
const ChatgptMcpDocsPage = lazy(() => import('./pages/McpMultiConnectorPages').then(m => ({ default: m.ChatgptMcpDocsPage })));
const McpSupportPage = lazy(() => import('./pages/McpMultiConnectorPages').then(m => ({ default: m.McpSupportPage })));
const McpTermsPage = lazy(() => import('./pages/McpMultiConnectorPages').then(m => ({ default: m.McpTermsPage })));

function ScrollToTop() { const { pathname } = useLocation(); useEffect(() => { window.scrollTo(0, 0); }, [pathname]); return null; }

function MetaPageViewTracker() {
  const { pathname, search } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    // The initial PageView is already fired inline in index.html to avoid a
    // duplicate hit on first paint; only track subsequent SPA route changes.
    if (first.current) { first.current = false; return; }
    import('./metaTracking').then(({ trackPageView }) => trackPageView()).catch(() => {});
  }, [pathname, search]);
  return null;
}

/* Client-side redirect for legacy /{lang}/integrazioni/* paths to the
   localized slug (integrations / integrationen / integraciones). */
function RedirectToLocalized({ prefix, slugWord }) {
  const { slug } = useParams();
  const target = slug ? `${prefix}/${slugWord}/${slug}` : `${prefix}/${slugWord}`;
  return <Navigate to={target} replace />;
}

/* Legacy URL redirect (client-side fallback for old Laravel paths).
   Mirrors the backend middleware in case the request reaches the frontend
   directly (CDN-only setup) instead of FastAPI. */
function LegacyRedirect() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const exact = {
      '/account/my-account': '/admin/login',
      '/account/login': '/admin/login',
      '/account/register': '/contatti',
      '/marketplace': '/integrazioni',
      '/marketplace-1': '/integrazioni',
      '/software-multi-marketplace': '/',
      '/privacy-policy': '/privacy',
      '/termini-e-condizioni': '/termini',
      '/contatto': '/contatti',
      '/onboarding-method': '/onboarding',
      // Legacy homepage variations
      '/home': '/',
      '/home-1': '/',
      '/home-2': '/',
      '/index': '/',
      '/index.html': '/',
      '/index.php': '/',
      // Legacy B2B / modules variations
      '/b2bmoduls': '/moduli',
      '/b2b-modules': '/moduli',
      '/b2b-modulus': '/moduli',
      '/b2b': '/moduli',
      '/modules': '/moduli',
      '/module': '/moduli',
      // EN-style URLs on root domain → IT canonical
      '/prices': '/prezzi',
      '/pricing': '/prezzi',
      '/contact': '/contatti',
      '/contact-us': '/contatti',
      '/support': '/supporto',
      '/help': '/supporto',
      '/about': '/',
      '/about-us': '/',
      '/integration': '/integrazioni',
      '/integrations': '/integrazioni',
      '/integrazioni-marketplace': '/integrazioni',
    };
    let target = exact[pathname];
    if (!target) {
      // Valid known language-prefixed routes (en/fr/de/es) → never redirect them.
      const LANG_PREFIXES = ['/en/', '/fr/', '/de/', '/es/'];
      const startsWithKnownLangPath = LANG_PREFIXES.some(p =>
        pathname.startsWith(p + 'blog/') ||
        pathname.startsWith(p + 'integrazioni/') ||
        pathname.startsWith(p + 'moduli/') ||
        pathname.startsWith(p + 'risorse/')
      );
      const validBaseLangPaths = ['', '/prezzi', '/onboarding', '/integrazioni', '/moduli',
        '/faq', '/supporto', '/blog', '/clienti', '/contatti', '/termini', '/privacy'];
      const isValidLangRoot = LANG_PREFIXES.some(p => {
        const stripped = p.slice(0, -1); // /en
        return pathname === stripped || pathname === p || validBaseLangPaths.some(sp => pathname === stripped + sp);
      });
      if (LANG_PREFIXES.some(p => pathname.startsWith(p)) && !isValidLangRoot && !startsWithKnownLangPath) {
        const prefix = pathname.slice(0, 3); // "/en", "/fr", etc.
        const rest = pathname.slice(3) || '/';
        const mapped = rest.replace('/marketplace-1', '/integrazioni').replace('/marketplace', '/integrazioni');
        target = mapped === '/' ? prefix : `${prefix}${mapped}`;
      } else if (pathname.startsWith('/knowledge/') || pathname === '/knowledge') {
        target = '/blog';
      } else if (pathname.startsWith('/account/') || pathname === '/account') {
        target = '/admin/login';
      }
    }
    if (target && target !== pathname) {
      navigate(target, { replace: true });
    }
  }, [pathname, navigate]);
  return null;
}

/* ───── Language Switcher ───── */
function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const current = LANG_LABELS[lang] || LANG_LABELS.it;
  return (
    <div className="dropdown lang-switcher" data-testid="lang-switcher-navbar">
      <button
        className="lang-switcher__btn d-flex align-items-center gap-2 border-0 bg-transparent"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label="Seleziona lingua"
      >
        <span className="lang-switcher__flag" aria-hidden="true">{current.flag}</span>
        <span className="lang-switcher__short fw-semibold">{current.short}</span>
        <i className="bi bi-chevron-down lang-switcher__chev"></i>
      </button>
      <ul className="dropdown-menu dropdown-menu-end lang-switcher__menu">
        {LANGS.map(code => {
          const label = LANG_LABELS[code];
          const active = code === lang;
          return (
            <li key={code}>
              <button
                type="button"
                onClick={() => setLang(code)}
                className={`dropdown-item d-flex align-items-center gap-2 ${active ? 'active' : ''}`}
                data-testid={`lang-option-${code}`}
              >
                <span aria-hidden="true">{label.flag}</span>
                <span>{label.name}</span>
                {active && <i className="bi bi-check2 ms-auto text-success"></i>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ───── Navbar ───── */
function Navbar() {
  const location = useLocation();
  const { t, lang } = useLang();
  const isA = (p) => location.pathname === p ? 'nav-link active' : 'nav-link';
  const menuCms = useSiteContent('risorse_menu');
  // In EN, skip CMS data (which is IT-only) and use translated fallback markup.
  const menuSections = lang === 'it' ? (menuCms.sections || null) : null;
  const risorse = { label: t.risorse.label };
  const ri = t.risorse.items;
  const rs = t.risorse.sezioni;

  const renderMenuItem = (item) => {
    if (!item.visible) return null;
    if (item.external) {
      return <a key={item.key} href={item.link} target="_blank" rel="noopener noreferrer" className="risorse-mega-menu__item"><i className={`bi ${item.icon}`}></i><div><span>{item.label}</span><small>{item.desc}</small></div></a>;
    }
    return <Link key={item.key} to={item.link} className="risorse-mega-menu__item"><i className={`bi ${item.icon}`}></i><div><span>{item.label}</span><small>{item.desc}</small></div></Link>;
  };

  const visibleSections = menuSections ? menuSections.filter(s => s.visible) : null;

  // Close mobile navbar on route change or scroll (fires only when it's open)
  useEffect(() => {
    const collapse = document.getElementById('mainNavbar');
    if (!collapse || !collapse.classList.contains('show')) return;
    // Prefer Bootstrap Collapse API for smooth close, fallback to class toggle
    const bs = window.bootstrap && window.bootstrap.Collapse;
    if (bs) {
      const inst = bs.getInstance(collapse) || new bs(collapse, { toggle: false });
      inst.hide();
    } else {
      collapse.classList.remove('show');
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const collapse = document.getElementById('mainNavbar');
        if (collapse && collapse.classList.contains('show')) {
          const bs = window.bootstrap && window.bootstrap.Collapse;
          if (bs) {
            const inst = bs.getInstance(collapse) || new bs(collapse, { toggle: false });
            inst.hide();
          } else {
            collapse.classList.remove('show');
          }
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close navbar when any nav-link inside it is clicked (covers anchor links,
  // dropdown items, and menu buttons). Uses event delegation on the collapse.
  useEffect(() => {
    const collapse = document.getElementById('mainNavbar');
    if (!collapse) return;
    const handler = (e) => {
      const target = e.target.closest('a, button');
      if (!target) return;
      // Ignore dropdown togglers — they should stay to reveal submenus
      if (target.classList.contains('dropdown-toggle')) return;
      if (!collapse.classList.contains('show')) return;
      const bs = window.bootstrap && window.bootstrap.Collapse;
      if (bs) {
        const inst = bs.getInstance(collapse) || new bs(collapse, { toggle: false });
        inst.hide();
      } else {
        collapse.classList.remove('show');
      }
    };
    collapse.addEventListener('click', handler);
    return () => collapse.removeEventListener('click', handler);
  }, []);

  // Desktop: close dropdown (Moduli, Risorse) when the mouse leaves the
  // .dropdown wrapper. Uses a short delay so brief cursor gaps between
  // trigger and mega-menu don't accidentally close it. Also closes the
  // dropdown when an inner mega-menu item is clicked (navigation).
  useEffect(() => {
    const nav = document.querySelector('nav.navbar');
    if (!nav) return;
    const dropdowns = nav.querySelectorAll('li.dropdown');
    const timers = new Map();
    const clearTimer = (el) => {
      const t = timers.get(el);
      if (t) { clearTimeout(t); timers.delete(el); }
    };
    const closeDropdown = (el) => {
      const toggle = el.querySelector('.dropdown-toggle');
      if (!toggle || !toggle.classList.contains('show')) return;
      const bs = window.bootstrap && window.bootstrap.Dropdown;
      if (bs) {
        const inst = bs.getInstance(toggle) || new bs(toggle);
        inst.hide();
      } else {
        toggle.classList.remove('show');
        const menu = el.querySelector('.dropdown-menu');
        if (menu) menu.classList.remove('show');
      }
    };
    const handleEnter = (e) => {
      const el = e.currentTarget;
      clearTimer(el);
      // Suppress reopening for a brief window after an inner click (prevents
      // the menu from popping back open when the user's cursor is still over
      // the trigger after a same-page navigation).
      if (el.dataset.suppressHover === '1') return;
      // Open dropdown on hover (desktop only — mobile touch fires click)
      const toggle = el.querySelector('.dropdown-toggle');
      if (!toggle) return;
      if (window.matchMedia && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (toggle.classList.contains('show')) return;
      const bs = window.bootstrap && window.bootstrap.Dropdown;
      if (bs) {
        const inst = bs.getInstance(toggle) || new bs(toggle);
        inst.show();
      } else {
        toggle.classList.add('show');
        const menu = el.querySelector('.dropdown-menu');
        if (menu) menu.classList.add('show');
      }
    };
    const handleLeave = (e) => {
      const el = e.currentTarget;
      clearTimer(el);
      const t = setTimeout(() => closeDropdown(el), 180);
      timers.set(el, t);
    };
    const handleInnerClick = (e) => {
      const el = e.currentTarget;
      // Only close if the click hits an actual link/button INSIDE the menu
      const link = e.target.closest('.dropdown-menu a, .dropdown-menu button');
      if (!link) return;
      clearTimer(el);
      // Close immediately, then briefly suppress hover-open to prevent the
      // menu from re-appearing while the cursor is still over the trigger.
      closeDropdown(el);
      el.dataset.suppressHover = '1';
      setTimeout(() => { delete el.dataset.suppressHover; }, 700);
    };
    dropdowns.forEach((d) => {
      d.addEventListener('mouseenter', handleEnter);
      d.addEventListener('mouseleave', handleLeave);
      d.addEventListener('click', handleInnerClick);
    });
    return () => {
      dropdowns.forEach((d) => {
        d.removeEventListener('mouseenter', handleEnter);
        d.removeEventListener('mouseleave', handleLeave);
        d.removeEventListener('click', handleInnerClick);
      });
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  return (
    <div className="navbar-shell py-3" data-testid="navbar-shell">
      <nav className="navbar fixed-top navbar-expand-xxl navbar-light shadow-sm rounded-pill px-3 px-lg-4">
        <div className="container-fluid px-0">
          <Link className="navbar-brand d-flex align-items-center gap-2" to="/" data-testid="navbar-brand">
            <img src="/frontend_assets/media/logos/logo-monogram.svg" alt="" height="28" width="28" /><span className="fw-semibold text-dark">Bonsai</span>
          </Link>
          <button className="navbar-toggler border-0" type="button" data-bs-toggle="collapse" data-bs-target="#mainNavbar" aria-label="Apri menu di navigazione" aria-controls="mainNavbar" data-testid="navbar-toggler"><span className="navbar-toggler-icon"></span></button>
          <div className="collapse navbar-collapse" id="mainNavbar">
            <ul className="navbar-nav ms-lg-3 gap-lg-3 align-items-lg-center">
              <li className="nav-item"><Link className={isA('/prezzi')} to="/prezzi">{t.nav.prezzi}</Link></li>
              {/* Moduli mega-menu */}
              <li className="nav-item dropdown risorse-dropdown moduli-dropdown" data-testid="moduli-dropdown">
                <Link className={isA('/moduli') + ' dropdown-toggle'} to="/moduli" role="button" data-bs-toggle="dropdown" data-bs-auto-close="true">{t.nav.moduli}</Link>
                <div className="dropdown-menu risorse-mega-menu p-4">
                  <div className="row g-4">
                    {MODULI_MENU.map((m, i) => {
                      const c = MODULES[m.slug] && (MODULES[m.slug][lang] || MODULES[m.slug].it);
                      const title = (c && c.hero_title) ? c.hero_title.replace(/\.$/, '') : m.fallbackTitle;
                      const desc = (c && c.hero_subtitle) ? c.hero_subtitle : (m.fallbackDesc || '');
                      return (
                        <div key={m.slug} className="col-md-6 col-lg-4">
                          <Link to={`/moduli/${m.slug}`} className="risorse-mega-menu__item" data-testid={`moduli-menu-${m.slug}`}>
                            <i className={`bi ${m.icon}`} style={{ color: m.accent }}></i>
                            <div>
                              <span>
                                {title}
                                {m.preview && (
                                  <sup style={{ fontSize: '0.55em', color: '#f59e0b', fontWeight: 700, marginLeft: '6px', letterSpacing: '0.05em', verticalAlign: 'super', lineHeight: 0, background: 'rgba(245,158,11,0.12)', padding: '2px 6px', borderRadius: '4px' }}>PREVIEW</sup>
                                )}
                              </span>
                              <small>{desc}</small>
                            </div>
                          </Link>
                        </div>
                      );
                    })}
                    <div className="col-12">
                      <Link to="/moduli" className="risorse-mega-menu__item risorse-mega-menu__item--all" data-testid="moduli-menu-all">
                        <i className="bi bi-grid-3x3-gap"></i>
                        <div><span>{t.nav.moduliAll}</span><small>{t.nav.moduliAllDesc}</small></div>
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
              <li className="nav-item"><Link className={isA('/onboarding')} to="/onboarding">{t.nav.onboarding}</Link></li>
              <li className="nav-item">
                <Link className={isA('/amazon-dsp')} to="/amazon-dsp" data-testid="nav-amazon-dsp" style={{ whiteSpace: 'nowrap' }}>
                  Amazon DSP<sup style={{ fontSize: '0.5em', color: '#FF9900', fontWeight: 700, marginLeft: '3px', letterSpacing: '0.04em', verticalAlign: 'super', lineHeight: 0, whiteSpace: 'nowrap' }}>NEW</sup>
                </Link>
              </li>
              <li className="nav-item dropdown risorse-dropdown integrazioni-dropdown" data-testid="integrazioni-dropdown">
                <Link className={isA('/integrazioni') + ' dropdown-toggle'} to="/integrazioni" role="button" data-bs-toggle="dropdown" data-bs-auto-close="true">{t.nav.integrazioni}</Link>
                <div className="dropdown-menu risorse-mega-menu p-4">
                  <div className="row g-4">
                    {INTEGRAZIONI_MENU.map((cat) => (
                      <div key={cat.id} className="col-md-6 col-lg-4">
                        <Link to={`/integrazioni?cat=${cat.id}`} className="risorse-mega-menu__item integrazioni-mega-menu__item" data-testid={`integrazioni-menu-${cat.id}`}>
                          <i className={`bi ${cat.icon}`} style={{ color: '#10b981' }}></i>
                          <div style={{ flex: 1 }}>
                            <span>{cat.label}</span>
                            <small>{cat.desc}</small>
                            <div className="integrazioni-mega-menu__logos" aria-hidden="true">
                              {(cat.logos || []).map((src, li) => (
                                <img key={`l-${li}`} src={src} alt="" loading="lazy" />
                              ))}
                              {(cat.logoIcons || []).map((ic, li) => (
                                <i key={`i-${li}`} className={`bi ${ic}`}></i>
                              ))}
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                    <div className="col-12">
                      <Link to="/integrazioni" className="risorse-mega-menu__item risorse-mega-menu__item--all" data-testid="integrazioni-menu-all">
                        <i className="bi bi-grid-3x3-gap"></i>
                        <div><span>Scopri tutte le integrazioni</span><small>Marketplace, corrieri, ERP, e-commerce e altro</small></div>
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
              {/* Risorse mega-menu */}
              <li className="nav-item dropdown risorse-dropdown" data-testid="risorse-dropdown">
                <a className="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" data-bs-auto-close="true">{risorse.label}</a>
                <div className="dropdown-menu risorse-mega-menu p-4">
                  <div className="row g-4">
                    {visibleSections ? visibleSections.map((section, si) => {
                      const colSize = Math.max(3, Math.floor(12 / visibleSections.length));
                      return (
                        <div key={si} className={`col-md-${colSize}`}>
                          <h6 className="risorse-mega-menu__category">{section.category}</h6>
                          {section.items.map(item => renderMenuItem(item))}
                        </div>
                      );
                    }) : (
                      <>
                        <div className="col-md-4">
                          <h6 className="risorse-mega-menu__category">{rs.scopri}</h6>
                          <Link to="/blog" className="risorse-mega-menu__item"><i className="bi bi-journal-text"></i><div><span>{ri.blog.label}</span><small>{ri.blog.desc}</small></div></Link>
                          <a href="https://docs.bonsaisolutions.it/" target="_blank" rel="noopener noreferrer" className="risorse-mega-menu__item"><i className="bi bi-file-earmark-code"></i><div><span>{ri.docs.label}</span><small>{ri.docs.desc}</small></div></a>
                          <Link to="/supporto" className="risorse-mega-menu__item"><i className="bi bi-life-preserver"></i><div><span>{ri.helpCenter.label}</span><small>{ri.helpCenter.desc}</small></div></Link>
                        </div>
                        <div className="col-md-4">
                          <h6 className="risorse-mega-menu__category">{rs.partner}</h6>
                          <Link to="/risorse/diventa-partner" className="risorse-mega-menu__item"><i className="bi bi-handshake"></i><div><span>{ri.diventaPartner.label}</span><small>{ri.diventaPartner.desc}</small></div></Link>
                          <h6 className="risorse-mega-menu__category mt-3">{rs.clienti}</h6>
                          <Link to="/risorse/casi-uso" className="risorse-mega-menu__item"><i className="bi bi-trophy"></i><div><span>{ri.casiUso.label}</span><small>{ri.casiUso.desc}</small></div></Link>
                          <Link to="/clienti" className="risorse-mega-menu__item" data-testid="menu-clienti"><i className="bi bi-people"></i><div><span>Clienti</span><small>Case study e recensioni verificate</small></div></Link>
                        </div>
                        <div className="col-md-4">
                          <h6 className="risorse-mega-menu__category">{rs.news}</h6>
                          <Link to="/risorse/news" className="risorse-mega-menu__item"><i className="bi bi-megaphone"></i><div><span>{ri.ultimeNotizie.label}</span><small>{ri.ultimeNotizie.desc}</small></div></Link>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </li>
            </ul>
            <div className="d-flex align-items-center justify-content-end ms-auto gap-3 mt-3 mt-lg-0">
              <LanguageSwitcher />
              <Link className="button-default px-4 rounded-pill text-decoration-none" to="/contatti" data-testid="cta-prova-bonsai" style={{ whiteSpace: 'nowrap' }}>{t.nav.cta}</Link>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}

/* ───── Pain Points ───── */
function PainPointsSection() {
  const { lang } = useLang();
  const isEn = lang === 'en';
  const cms = useSiteContent('pain_points');
  const useCms = !isEn;
  const defaultProblems = [
    { icon: 'bi-files', text: 'Cataloghi duplicati e disallineati tra canali', hover: 'Aggiornamenti manuali su ogni canale. Ore perse, errori garantiti.' },
    { icon: 'bi-exclamation-triangle', text: 'Stock errati che causano overselling o rotture', hover: 'Rischi overselling e recensioni negative.' },
    { icon: 'bi-hand-index', text: 'Ordini e spedizioni ancora gestiti manualmente', hover: 'Ogni passaggio manuale è un potenziale ritardo o errore.' },
    { icon: 'bi-trophy', text: 'Prodotti che perdono la Buy Box senza controllo', hover: 'Le vendite vanno ai competitor. E non te ne accorgi.' },
    { icon: 'bi-eye-slash', text: 'Errori di pubblicazione che scopri troppo tardi', hover: 'Prodotti invisibili o penalizzati. Fatturato perso.' },
    { icon: 'bi-question-circle', text: 'Nessuna visione chiara su margini e performance', hover: 'Vendi, ma non sai se stai guadagnando davvero.' },
  ];
  const problems = cms.items || defaultProblems;
  const title = (useCms && cms.title) || (isEn ? 'Are you managing your marketplaces like this?' : 'Stai gestendo i tuoi marketplace così?');
  const conclusion = (useCms && cms.conclusion) || (isEn ? 'Bonsai eliminates operational chaos and centralizes everything in one system.' : 'Bonsai elimina il caos operativo e centralizza tutto in un unico sistema.');

  return (
    <section className="py-6 pain-section" data-testid="pain-points-section">
      <div className="container py-4">
        <div className="text-center mb-5">
          <h2 className="fw-bold display-5 mb-2">{title}</h2>
        </div>
        <div className="row g-4 justify-content-center mb-5">
          {problems.map((p, i) => (
            <div key={i} className="col-md-6 reveal" style={{transitionDelay:`${i*0.07}s`}}>
              <div className="pain-card" data-testid={`pain-${i}`}>
                <div className="pain-card__icon">
                  <i className={`bi ${p.icon}`}></i>
                </div>
                <div className="pain-card__content">
                  <p className="pain-card__text">{p.text}</p>
                  <p className="pain-card__hover">{p.hover}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center">
          <p className="pain-conclusion mb-4">{conclusion}</p>
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <Link to="/moduli" className="btn btn-success btn-lg px-5 py-3 cta-primary" data-testid="pain-cta-moduli">
              <i className="bi bi-arrow-right me-2"></i>{isEn ? 'See how it works' : 'Scopri come funziona'}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Why Modular ───── */
function WhyModularSection() {
  const { t, lang } = useLang();
  const isEn = lang === 'en';
  const cms = useSiteContent('why_choose_us');
  const useCms = !isEn;
  const defSystem = [
    { icon: 'bi-boxes', title: 'Modulare', desc: 'Attiva 1 modulo o tutti 8.' },
    { icon: 'bi-infinity', title: 'Licenze illimitate', desc: 'Paesi e utenti senza limiti.' },
    { icon: 'bi-database-gear', title: 'ERP integrati', desc: 'SAP, Zucchetti, Sistemi, Dynamics, TeamSystem.' },
    { icon: 'bi-piggy-bank', title: 'Budgeting e Forecasting', desc: 'Pianificazione budget cross-modulo su ordini, spedizioni e margini.' },
    { icon: 'bi-diagram-3', title: 'Mapping Categorie', desc: 'Da quelle della tua azienda a quelle di Amazon e marketplace con un click!' },
    { icon: 'bi-stars', title: 'AI Contenuti', desc: "Automatizza tutta la creazione dei contenuti con l'AI." },
  ];
  const defIncluded = [
    { icon: 'bi-mortarboard', title: 'Formazione operatori', desc: 'Formazione ad operatori che utilizzano il sistema.' },
    { icon: 'bi-sliders', title: 'Customizzazione moduli', desc: 'Customizzazione dei moduli in relazione a necessità specifiche.' },
    { icon: 'bi-chat-dots', title: 'Supporto continuo', desc: 'NO TICKETING. Supporto diretto e continuo.' },
    { icon: 'bi-shield-lock', title: 'Consulenza prioritaria', desc: 'Consulenza e insight prioritari da contatti interni a marketplace per risoluzione problematiche o insights di settore.' },
  ];
  const systemPills = cms.system_items || defSystem;
  const includedPills = cms.included_items || defIncluded;
  const sectionTitle = (useCms && cms.title) || (isEn ? 'Why choose us?' : 'Perché scegliere noi?');
  const includedLabel = (useCms && cms.included_label) || (isEn ? 'Human support, always present.' : 'Supporto umano, sempre presente.');
  const systemLabel = (useCms && cms.system_label) || (isEn ? 'Every feature is designed to increase your sales.' : 'Ogni feature sviluppata è pensata per far aumentare le tue vendite.');
  return (
    <section className="py-6 why-modular-section" data-testid="why-modular-section">
      <div className="container py-4">
        <div className="text-center mb-5">
          <h2 className="fw-bold display-5 mb-3">{sectionTitle}</h2>
        </div>

        {/* Included / human support */}
        <div className="why-modular-divider mb-4">
          <span className="why-modular-category-label why-modular-category-label--included">
            <i className="bi bi-people-fill me-2"></i>{includedLabel}
          </span>
        </div>
        <div className="row g-4 mb-5">
          {includedPills.map((p, i) => (
            <div key={i} className="col-md-6 col-lg-3 reveal" style={{transitionDelay:`${i*0.06}s`}}>
              <div className="why-modular-card why-modular-card--included h-100 rounded-4 p-4 text-center">
                <div className="why-modular-icon why-modular-icon--included mx-auto mb-3">
                  <i className={`bi ${p.icon}`}></i>
                </div>
                <h3 className="fw-bold mb-2 fs-6">{p.title}</h3>
                <p className="small text-white-50 mb-2">{p.desc}</p>
                <span className="badge why-modular-included-badge">{t.whyModular.alwaysIncluded}</span>
              </div>
            </div>
          ))}
        </div>

        {/* System features */}
        <div className="why-modular-divider mb-4">
          <span className="why-modular-category-label why-modular-category-label--system">
            <i className="bi bi-lightning-charge-fill me-2"></i>{systemLabel}
          </span>
        </div>
        <div className="row g-4">
          {systemPills.map((p, i) => (
            <div key={i} className="col-md-6 col-lg-4 reveal" style={{transitionDelay:`${i*0.06}s`}}>
              <div className="why-modular-card why-modular-card--system h-100 rounded-4 p-4">
                <div className="d-flex align-items-start gap-3">
                  <div className="why-modular-icon why-modular-icon--system flex-shrink-0">
                    <i className={`bi ${p.icon}`}></i>
                  </div>
                  <div>
                    <h3 className="fw-bold mb-1 fs-6">{p.title}</h3>
                    <p className="small text-white-50 mb-0">{p.desc}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Hero ───── */
function HeroSection() {
  const { t, lang } = useLang();
  const cms = useSiteContent('hero');
  // In EN, prefer translations over CMS (CMS data is IT-only).
  const useCms = lang === 'it';
  const badge = (useCms && cms.badge) || t.hero.badge;
  const title = (useCms && cms.title) || t.hero.title;
  const subtitle = (useCms && cms.subtitle) || t.hero.subtitle;
  const cta = (useCms && cms.cta) || t.hero.cta;
  const feat1 = (useCms && cms.feat1) || t.hero.feat1;
  const feat2 = (useCms && cms.feat2) || t.hero.feat2;
  return (
    <section className="hero-section bg-light py-4 default-margin-top" data-testid="hero-section">
      <div className="hero-mesh"></div>
      <div className="container py-4 position-relative" style={{zIndex:2}}>
        <div className="row align-items-center g-4">
          <div className="col-12 py-2">{t.hero.topline && <h1 className="text-dark fw-bold hero-topline">{t.hero.topline}</h1>}</div>
          <div className="col-lg-6 text-black">
            <p className="text-uppercase text-success fw-semibold mb-2 hero-badge-anim">{badge}</p>
            <h2 className="display-4 fw-bold mb-3 hero-gradient-text">{title}</h2>
            <p className="fs-5 mb-4 hero-subtitle-anim">{subtitle}</p>
            <div className="d-flex flex-wrap gap-3 mb-3 hero-cta-anim">
              <Link to="/contatti" className="btn btn-success btn-lg px-5 py-3 cta-primary magnetic-btn" data-testid="hero-cta-demo"><i className="bi bi-play-circle me-2"></i>{cta}</Link>
              <a
                href="/#messaggi-ai"
                className="btn btn-lg px-5 py-3 hero-cta-messaggi-ai magnetic-btn"
                data-testid="hero-cta-messaggi-ai"
                onClick={(e) => {
                  const target = document.getElementById('messaggi-ai');
                  if (target) {
                    e.preventDefault();
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                <i className="bi bi-chat-dots me-2" aria-hidden="true"></i>
                Scopri customer chat AI
                <span className="hero-cta-novita-badge" aria-label="Novità">Novità</span>
              </a>
            </div>
            <ul className="list-inline small mb-0 hero-feats-anim">
              <li className="list-inline-item me-3"><i className="bi bi-check-circle text-success me-1"></i>{feat1}</li>
              <li className="list-inline-item"><i className="bi bi-check-circle text-success me-1"></i>{feat2}</li>
            </ul>
            <div className="hero-review-badges" data-testid="hero-review-badges">
              <span className="hero-review-badges-label">Certificati</span>
              <a href="https://www.capterra.com/p/10043476/Bonsai/reviews/" target="_blank" rel="noopener noreferrer" aria-label="Recensioni Bonsai su Capterra">
                <img src="https://brand-assets.capterra.com/badge/3806a48a-1dee-4fdd-ba20-425ee2d5baee.svg" alt="Capterra Reviews" loading="lazy" width="150" height="68" />
              </a>
              <img src="https://customer-assets-lqy194kg.emergentagent.net/job_site-importer-6/artifacts/g2854e7l_amazon-ads-partner.webp" alt="Amazon Ads Verified Partner" loading="lazy" width="120" height="72" className="hero-partner-badge-img" />
              <img src="https://customer-assets-lqy194kg.emergentagent.net/job_site-importer-6/artifacts/vrggfkba_amazon-software-partners.webp" alt="Amazon Selling Partner Appstore — Software Partner" loading="lazy" width="120" height="72" className="hero-partner-badge-img" />
              <Link to="/docs/claude-mcp-connector" className="hero-claude-badge" aria-label={lang === 'it' ? 'Bonsai è disponibile su Claude, ChatGPT, Copilot e Gemini' : 'Bonsai is available on Claude, ChatGPT, Copilot and Gemini'} data-testid="hero-claude-badge">
                <span className="hero-claude-badge__label">{lang === 'it' ? 'Disponibile su' : 'Available on'}</span>
                <span className="hero-claude-badge__logos">
                  <img src="/frontend_assets/media/images/partners/claude-logo.webp" alt="Claude (Anthropic)" loading="lazy" width="229" height="50" className="hero-claude-badge__logo hero-claude-badge__logo--claude" />
                  <img src="/frontend_assets/media/images/partners/openai-logo.svg" alt="ChatGPT (OpenAI)" loading="lazy" width="24" height="24" className="hero-claude-badge__logo hero-claude-badge__logo--icon" />
                  <img src="/frontend_assets/media/images/partners/copilot-logo.svg" alt="Copilot (Microsoft)" loading="lazy" width="24" height="24" className="hero-claude-badge__logo hero-claude-badge__logo--icon" />
                  <img src="/frontend_assets/media/images/partners/gemini-logo.svg" alt="Gemini (Google)" loading="lazy" width="24" height="24" className="hero-claude-badge__logo hero-claude-badge__logo--icon" />
                </span>
              </Link>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="hero-video-wrap tilt-card" data-testid="hero-dashboard">
              <div className="card shadow-lg border-0 overflow-hidden position-relative">
                <div className="card-body p-0 bg-dark" style={{borderRadius:'0.5rem'}}>
                  <img
                    src="/frontend_assets/media/images/hero-rank-price.webp"
                    alt="Bonsai Rank & Price — dashboard Buy Box, prezzi e trend competitor su Amazon"
                    className="img-fluid rounded-3 shadow w-100"
                    width="1200"
                    height="630"
                    loading="eager"
                    fetchpriority="high"
                    data-testid="hero-video"
                  />
                  <Link to="/contatti" className="hero-dashboard-overlay" data-testid="hero-dashboard-overlay">
                    <div className="hero-dashboard-overlay__content">
                      <i className="bi bi-play-circle"></i>
                      <span>{t.hero.dashboardOverlay}</span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Count-up hook for smooth number transitions ───── */
function useCountUp(target, duration = 500) {
  const [display, setDisplay] = React.useState(target);
  const rafRef = React.useRef(null);
  const prevRef = React.useRef(target);
  React.useEffect(() => {
    const from = prevRef.current;
    const to = target;
    if (from === to) return;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = Math.round(from + (to - from) * eased);
      setDisplay(val);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else prevRef.current = to;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return display;
}

/* ───── Messaggi AI section (replaces ROI Calculator on homepage) ───── */
function MessaggiAI() {
  const [orders, setOrders] = React.useState(200);
  const msgDay = orders * 0.175;
  const msgMonth = Math.round(msgDay * 30);
  const msgYear = Math.round(msgDay * 365);
  const saving = Math.round(msgYear * 0.47);
  const animMonth = useCountUp(msgMonth);
  const animSaving = useCountUp(saving);
  const fmt = (n) => n.toLocaleString('it-IT');
  const ordersLabel = orders >= 1000 ? '1000+' : orders;

  const scrollToHow = (e) => {
    e.preventDefault();
    document.getElementById('ai-msg-how')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="messaggi-ai" className="ai-msg-section" data-testid="messaggi-ai-section">
      <div className="container">
        <div className="ai-msg-header text-center">
          <span className="ai-msg-badge" data-testid="ai-msg-badge">Novità</span>
          <h2 className="ai-msg-title">Messaggi AI</h2>
          <p className="ai-msg-sub">Un operatore che non dorme mai, su ogni marketplace in cui vendi.</p>
          <p className="ai-msg-desc">
            Messaggi AI legge, capisce e risponde automaticamente <strong>con controllo umano</strong> ai messaggi dei clienti su <mark className="ai-msg-highlight">Amazon Seller, Amazon Vendor, Temu, ManoMano, TikTok Shop, eBay e ogni nuovo marketplace collegato</mark> — gestendo richieste tipo &ldquo;dov&rsquo;è il mio ordine&rdquo;, tracking, resi e prodotti danneggiati in tempo reale, senza intervento umano nella maggior parte dei casi.
          </p>
          <p className="ai-msg-accent">Non è un chatbot. È il tuo team di customer care, moltiplicato.</p>
        </div>

        <div className="ai-msg-preview" data-testid="ai-msg-preview">
          <div className="ai-msg-preview-frame">
            <div className="ai-msg-preview-bar" aria-hidden="true">
              <span className="ai-msg-preview-dot ai-msg-preview-dot--r"></span>
              <span className="ai-msg-preview-dot ai-msg-preview-dot--y"></span>
              <span className="ai-msg-preview-dot ai-msg-preview-dot--g"></span>
            </div>
            <img
              src="/frontend_assets/media/images/messaggi-ai-preview.webp"
              alt="Anteprima interfaccia Messaggi AI di Bonsai — inbox unificata dei ticket clienti dai marketplace collegati"
              className="ai-msg-preview-img"
              loading="lazy"
              width="1600"
              height="900"
            />
          </div>
        </div>

        <div className="ai-msg-calculator" data-testid="ai-msg-calculator">
          <div className="ai-msg-calc-top">
            <label htmlFor="ai-msg-orders" className="ai-msg-calc-label">Quanti ordini gestisci al giorno?</label>
            <div className="ai-msg-orders-value" data-testid="ai-msg-orders-value">{ordersLabel}</div>
          </div>
          <input
            id="ai-msg-orders"
            type="range"
            min="0"
            max="1000"
            step="10"
            value={orders}
            onChange={(e) => setOrders(parseInt(e.target.value, 10))}
            className="ai-msg-slider"
            data-testid="ai-msg-orders-slider"
            aria-label="Ordini gestiti al giorno"
          />
          <div className="ai-msg-slider-marks" aria-hidden="true">
            <span>0</span><span>250</span><span>500</span><span>750</span><span>1000+</span>
          </div>

          <div className="ai-msg-results">
            <div className="ai-msg-result-card">
              <div className="ai-msg-result-label">Messaggi gestiti al mese</div>
              <div className="ai-msg-result-value" data-testid="ai-msg-messages-month">{fmt(animMonth)}</div>
            </div>
            <div className="ai-msg-result-card ai-msg-result-card--saving">
              <div className="ai-msg-result-label">Risparmio annuo stimato</div>
              <div className="ai-msg-result-value" data-testid="ai-msg-saving-year">{fmt(animSaving)} €</div>
            </div>
          </div>

          <p className="ai-msg-note">
            Stima basata su un caso reale: un&rsquo;azienda con 1.000 ordini/giorno gestiva 150–200 messaggi clienti al giorno con una risorsa dedicata da 30.000 €/anno.
          </p>

          <div className="ai-msg-cta-row">
            <Link to="/contatti" className="ai-msg-cta-primary" data-testid="ai-msg-cta-demo">
              <i className="bi bi-calendar-check me-2" aria-hidden="true"></i>Prenota una demo
            </Link>
            <a href="#ai-msg-how" className="ai-msg-cta-secondary" data-testid="ai-msg-cta-how" onClick={scrollToHow}>
              Scopri come funziona <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        <div id="ai-msg-how" className="ai-msg-how" data-testid="ai-msg-how">
          <h3 className="ai-msg-how-title">Come funziona</h3>
          <ol className="ai-msg-steps">
            <li className="ai-msg-step" data-testid="ai-msg-step-1">
              <div className="ai-msg-step-num">1</div>
              <div className="ai-msg-step-body">
                <div className="ai-msg-step-title">Riceve</div>
                <p>Tutti i messaggi da ogni marketplace collegato confluiscono in un&rsquo;unica coda.</p>
              </div>
            </li>
            <li className="ai-msg-step" data-testid="ai-msg-step-2">
              <div className="ai-msg-step-num">2</div>
              <div className="ai-msg-step-body">
                <div className="ai-msg-step-title">Capisce</div>
                <p>L&rsquo;AI classifica la richiesta: tracking, reso, danno o domanda generica.</p>
              </div>
            </li>
            <li className="ai-msg-step" data-testid="ai-msg-step-3">
              <div className="ai-msg-step-num">3</div>
              <div className="ai-msg-step-body">
                <div className="ai-msg-step-title">Risponde</div>
                <p>In automatico, con i dati reali dell&rsquo;ordine e nel tono giusto.</p>
              </div>
            </li>
            <li className="ai-msg-step" data-testid="ai-msg-step-4">
              <div className="ai-msg-step-num">4</div>
              <div className="ai-msg-step-body">
                <div className="ai-msg-step-title">Scala</div>
                <p>Se il caso è complesso, passa la mano a un operatore umano.</p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ───── Legacy ROI Calculator (kept for reference; not rendered) ───── */
function ROICalculator() {
  const [orders, setOrders] = React.useState(80);
  const [marketplaces, setMarketplaces] = React.useState(3);
  const [skus, setSkus] = React.useState(500);
  const [view, setView] = React.useState('month'); // 'month' | 'year'

  // ─── Derived benchmarks (auto-calculated, never user-editable) ───
  const minutesPerOrder = Math.min(15 + (marketplaces - 1) * 2, 25);
  const hourlyCost = orders < 30 ? 15 : orders < 100 ? 18 : orders < 300 ? 21 : 24;
  const errorRate = Math.min(0.05 + (marketplaces - 1) * 0.015, 0.13);
  const WORK_DAYS = 22;
  const TIME_SAVING_RATE = 0.87;
  const COST_PER_ERROR = 80;

  // ─── Monthly calculation ───
  const ordersMonth = orders * WORK_DAYS;
  const hoursSavedMonth = (ordersMonth * minutesPerOrder * TIME_SAVING_RATE) / 60;
  const laborSaving = hoursSavedMonth * hourlyCost;
  const errorRecovery = ordersMonth * errorRate * COST_PER_ERROR;
  const multichannelBonus = marketplaces > 1
    ? (marketplaces - 1) * 0.5 * hourlyCost * WORK_DAYS
    : 0;
  const catalogSync = (skus * marketplaces * 0.3 / 60) * hourlyCost * 4;

  const totalMonth = laborSaving + errorRecovery + multichannelBonus + catalogSync;
  const totalYear = totalMonth * 12;

  const total = view === 'month' ? totalMonth : totalYear;
  const totalHours = view === 'month' ? hoursSavedMonth : hoursSavedMonth * 12;
  const totalOrders = view === 'month' ? ordersMonth : ordersMonth * 12;
  const laborSavingDisplay = view === 'month' ? laborSaving : laborSaving * 12;
  const errorRecoveryDisplay = view === 'month' ? errorRecovery : errorRecovery * 12;
  const multichannelDisplay = view === 'month' ? multichannelBonus : multichannelBonus * 12;
  const catalogSyncDisplay = view === 'month' ? catalogSync : catalogSync * 12;

  const fmt = (n) => Math.round(n).toLocaleString('it-IT');
  const fmtEur = (n) => `€ ${fmt(n)}`;

  const bumpOrders = (delta) => setOrders((o) => Math.max(0, o + delta));
  const bumpSkus = (delta) => setSkus((s) => Math.max(0, s + delta));

  const periodLabel = view === 'month' ? 'mese' : 'anno';

  return (
    <section className="roi-calc-section" data-testid="roi-calculator-section">
      <div className="container">
        <div className="roi-calc-header mb-3 text-center">
          <h2 className="fw-bold mb-1 roi-calc-title mx-auto">
            Scopri quanto Bonsai può <em className="roi-calc-em">farti guadagnare</em> ogni anno.
          </h2>
          <p className="roi-calc-sub mb-0 mx-auto">
            Inserisci solo i dati base del tuo business. Le stime si basano su benchmark reali di settore.
          </p>
        </div>

        <div className="row g-3 roi-calc-grid">
          {/* LEFT: inputs + auto-estimates */}
          <div className="col-lg-7">
            <div className="roi-calc-inputs">
              {/* Orders per day */}
              <div className="roi-field" data-testid="roi-orders-field">
                <label className="roi-label" htmlFor="roi-orders-input"><i className="bi bi-box-seam me-2"></i>Ordini al giorno</label>
                <div className="roi-stepper">
                  <button type="button" className="roi-step-btn" onClick={() => bumpOrders(-10)} data-testid="roi-orders-minus" aria-label="Diminuisci ordini di 10">−</button>
                  <input
                    id="roi-orders-input"
                    type="number"
                    className="roi-step-input"
                    value={orders}
                    onChange={(e) => setOrders(Math.max(0, parseInt(e.target.value) || 0))}
                    data-testid="roi-orders-input"
                    aria-label="Ordini al giorno"
                    min="0"
                  />
                  <button type="button" className="roi-step-btn" onClick={() => bumpOrders(10)} data-testid="roi-orders-plus" aria-label="Aumenta ordini di 10">+</button>
                </div>
              </div>

              {/* Marketplaces slider */}
              <div className="roi-field" data-testid="roi-marketplaces-field">
                <label className="roi-label" htmlFor="roi-marketplaces-slider"><i className="bi bi-grid me-2"></i>Marketplace attivi</label>
                <input
                  id="roi-marketplaces-slider"
                  type="range"
                  min="1"
                  max="8"
                  step="1"
                  value={marketplaces}
                  onChange={(e) => setMarketplaces(parseInt(e.target.value))}
                  className="roi-slider"
                  data-testid="roi-marketplaces-slider"
                  aria-label="Marketplace attivi"
                  aria-valuemin="1"
                  aria-valuemax="8"
                  aria-valuenow={marketplaces}
                />
                <div className="roi-slider-value">{marketplaces} marketplace</div>
              </div>

              {/* SKUs */}
              <div className="roi-field" data-testid="roi-skus-field">
                <label className="roi-label" htmlFor="roi-skus-input"><i className="bi bi-tags me-2"></i>Prodotti a catalogo</label>
                <div className="roi-stepper">
                  <button type="button" className="roi-step-btn" onClick={() => bumpSkus(-100)} data-testid="roi-skus-minus" aria-label="Diminuisci SKU di 100">−</button>
                  <input
                    id="roi-skus-input"
                    type="number"
                    className="roi-step-input"
                    value={skus}
                    onChange={(e) => setSkus(Math.max(0, parseInt(e.target.value) || 0))}
                    data-testid="roi-skus-input"
                    aria-label="Prodotti a catalogo (SKU)"
                    min="0"
                  />
                  <button type="button" className="roi-step-btn" onClick={() => bumpSkus(100)} data-testid="roi-skus-plus" aria-label="Aumenta SKU di 100">+</button>
                </div>
              </div>

              {/* Auto-estimates panel */}
              <div className="roi-estimates" data-testid="roi-estimates-panel">
                <p className="roi-estimates-title">STIME AUTOMATICHE APPLICATE</p>
                <div className="roi-estimate-row">
                  <div>
                    <div className="roi-estimate-name">Minuti/ordine (manuale)</div>
                    <div className="roi-estimate-source">PLGGR · benchmark settore B2C</div>
                  </div>
                  <div className="roi-estimate-value">{minutesPerOrder} min</div>
                </div>
                <div className="roi-estimate-row">
                  <div>
                    <div className="roi-estimate-name">Tasso errori ordini manuali</div>
                    <div className="roi-estimate-source">APQC · studio su 2.561 aziende</div>
                  </div>
                  <div className="roi-estimate-value">{Math.round(errorRate * 100)}%</div>
                </div>
                <div className="roi-estimate-row">
                  <div>
                    <div className="roi-estimate-name">Costo orario team stimato</div>
                    <div className="roi-estimate-source">Eurostat 2024 · PMI italiane</div>
                  </div>
                  <div className="roi-estimate-value">€ {hourlyCost}/h</div>
                </div>
                <div className="roi-estimate-row">
                  <div>
                    <div className="roi-estimate-name">Risparmio tempo con automazione</div>
                    <div className="roi-estimate-source">AmerCareRoyal case study · 2026</div>
                  </div>
                  <div className="roi-estimate-value">87%</div>
                </div>
                <div className="roi-estimate-row">
                  <div>
                    <div className="roi-estimate-name">Riduzione costi fulfillment</div>
                    <div className="roi-estimate-source">Streamline Retail · 2026</div>
                  </div>
                  <div className="roi-estimate-value">fino a −28%</div>
                </div>
              </div>

              <p className="roi-disclaimer mt-3 mb-0">
                * Benchmark da APQC, PLGGR, Eurostat e Streamline Retail. I risultati effettivi variano in base alla tua operatività specifica.
              </p>
            </div>
          </div>

          {/* RIGHT: result panel */}
          <div className="col-lg-5">
            <div className="roi-result-panel" data-testid="roi-result-panel">
              <div className="roi-toggle">
                <button
                  type="button"
                  className={`roi-toggle-btn ${view === 'month' ? 'active' : ''}`}
                  onClick={() => setView('month')}
                  data-testid="roi-toggle-month"
                >Mese</button>
                <button
                  type="button"
                  className={`roi-toggle-btn ${view === 'year' ? 'active' : ''}`}
                  onClick={() => setView('year')}
                  data-testid="roi-toggle-year"
                >Anno</button>
              </div>

              <p className="roi-result-label">Con Bonsai potresti risparmiare:</p>
              <div className="roi-result-amount" data-testid="roi-result-amount">
                +€ {fmt(total)}
              </div>

              <p className="roi-result-desc">
                Automatizzando {fmt(totalOrders)} ordini/{periodLabel} su {marketplaces} {marketplaces === 1 ? 'canale' : 'canali'}, Bonsai libera <strong>{fmt(totalHours)} ore operative</strong>.
              </p>

              <div className="roi-divider"></div>

              <ul className="roi-breakdown" data-testid="roi-breakdown">
                <li>
                  <span>Ore ops risparmiate</span>
                  <span className="roi-bd-value">{fmt(totalHours)} h</span>
                </li>
                <li>
                  <span>Risparmio costo lavoro</span>
                  <span className="roi-bd-value">{fmtEur(laborSavingDisplay)}</span>
                </li>
                <li>
                  <span>Recupero da errori/resi</span>
                  <span className="roi-bd-value">{fmtEur(errorRecoveryDisplay)}</span>
                </li>
                <li>
                  <span>Efficienza multi-canale</span>
                  <span className="roi-bd-value">{marketplaces === 1 ? 'n/d' : fmtEur(multichannelDisplay)}</span>
                </li>
                <li>
                  <span>Sync catalogo automatico</span>
                  <span className="roi-bd-value">{fmtEur(catalogSyncDisplay)}</span>
                </li>
              </ul>

              <a
                href="https://www.bonsaigrowth.it/contatti"
                target="_blank"
                rel="noopener noreferrer"
                className="roi-cta"
                data-testid="roi-cta-demo"
              >
                Richiedi una Demo <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Social Proof Carousel ───── */
function SocialProof() {
  const [active, setActive] = React.useState(0);
  const testimonials = [
    {
      badge: 'Risultati', title: 'Primi 3 mesi',
      stats: [
        { value: '18', prefix: '+', suffix: 'k', label: 'Ordini/giorno' },
        { value: '32', prefix: '-', suffix: '%', label: 'Errori picking' },
        { value: '99.9', prefix: '-', suffix: '%', label: 'Errori scheda prodotto', decimals: 1 },
      ],
      quote: 'Ordini Amazon e Temu unificati. Errori picking ridotti del 25% in 2 mesi.',
      author: 'Alberto, COO Baroni Home',
      scaleTitle: 'Scala senza attriti',
      scaleItems: ['+100k varianti', '5k spedizioni/giorno', 'Assistenza con risposta entro 10 minuti', 'Modulare e scalabile. Il sistema cresce con le tue vendite.'],
    },
    {
      badge: 'Risultati', title: 'Primi 3 mesi',
      stats: [
        { value: '40', prefix: '+', suffix: 'k', label: 'Ordini/giorno' },
        { value: '100', prefix: '-', suffix: '%', label: 'Errori pubblicazione prodotti' },
        { value: '9', prefix: '+', suffix: '%', label: 'Aumento marginalità' },
      ],
      quote: 'Abbiamo trovato la soluzione definitiva per le nostre vendite su marketplace.',
      author: 'Lorenzo, CEO Rebecca Srl',
      scaleTitle: 'Scala senza attriti',
      scaleItems: ['+100k varianti', '5k spedizioni/giorno', 'Assistenza con risposta entro 10 minuti', 'Modulare e scalabile. Il sistema cresce con le tue vendite.'],
    },
    {
      badge: 'Risultati', title: 'Primi 3 mesi',
      stats: [
        { value: '90', prefix: '+', suffix: 'k', label: 'Ordini/giorno' },
        { value: '60', prefix: '-', suffix: 'k', label: 'Prodotti pubblicati' },
        { value: '100', prefix: '-', suffix: '%', label: 'Errori di pubblicazione' },
      ],
      quote: "Prima di Bonsai era un delirio. Impossibile pubblicare su Amazon ed eBay un catalogo ampio come il nostro, sia per categorie che per ASIN.",
      author: 'Simone, COO Pulcranet Srl',
      scaleTitle: 'Scala senza attriti',
      scaleItems: ['+100k varianti', '5k spedizioni/giorno', 'Assistenza con risposta entro 10 minuti', 'Modulare e scalabile. Il sistema cresce con le tue vendite.'],
    },
  ];

  React.useEffect(() => {
    const timer = setInterval(() => setActive(a => (a + 1) % testimonials.length), 6000);
    return () => clearInterval(timer);
  }, []);

  const t = testimonials[active];

  return (
    <section className="py-6 bg-dark text-light" data-testid="social-proof-section" style={{position:'relative',overflow:'hidden'}}>
      <div className="morph-blob" style={{width:350,height:350,top:'-80px',right:'-60px',background:'rgba(0,188,212,0.12)'}}></div>
      <div className="morph-blob" style={{width:250,height:250,bottom:'-60px',left:'10%',background:'rgba(167,139,250,0.08)',animationDelay:'3s'}}></div>
      <div className="container py-4">
        <div className="row g-4 align-items-center">
          <div className="col-lg-7">
            <div className="proof-carousel" key={active}>
              <p className="text-uppercase text-success fw-semibold mb-2">{t.badge}</p>
              <h2 className="fw-bold mb-4">{t.title}</h2>
              <div className="d-flex flex-wrap gap-4 mb-4">
                {t.stats.map((s, i) => (
                  <div key={i} className="text-center">
                    <p className="display-5 fw-bold mb-0">{s.prefix}{s.value}{s.suffix}</p>
                    <p className="small text-white-50 mb-0">{s.label}</p>
                  </div>
                ))}
              </div>
              <figure className="card bg-body text-dark border-0 shadow-sm mb-3"><blockquote className="card-body mb-0">
                <p className="fw-semibold mb-1">{t.quote}</p><figcaption className="small mb-0">&mdash; {t.author}</figcaption>
              </blockquote></figure>
              <div className="d-flex gap-2">
                {testimonials.map((_, i) => (
                  <button key={i} className={`proof-dot${i === active ? ' active' : ''}`} onClick={() => setActive(i)} data-testid={`proof-dot-${i}`} aria-label={`Testimonianza ${i+1}`}></button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card h-100 border-success border-2 bg-transparent text-white"><div className="card-body p-4">
              <div className="d-flex align-items-center mb-3"><div className="bg-success bg-opacity-10 rounded-circle p-2 me-3"><i className="bi bi-rocket-takeoff-fill text-success fs-4"></i></div><h3 className="fw-bold mb-0 text-white fs-5">{t.scaleTitle}</h3></div>
              <ul className="list-unstyled mb-0">{t.scaleItems.map((text,i)=>(<li key={i} className="d-flex align-items-start mb-2"><i className="bi bi-check2-circle text-success me-2 mt-1"></i><span className="text-white">{text}</span></li>))}</ul>
            </div></div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Clients Marquee ───── */
function ClientsMarquee() {
  const { t, lang } = useLang();
  const cms = useSiteContent('clients');
  const useCms = lang === 'it';
  const defaultClients = [
    { src: '/frontend_assets/media/images/main-clients/cavalieri.avif', alt: 'Cavalieri' },
    { src: '/frontend_assets/media/images/main-clients/piccini.avif', alt: 'Piccini 1882' },
    { src: '/frontend_assets/media/images/main-clients/baroni.avif', alt: 'Baroni Home' },
    { src: '/frontend_assets/media/images/main-clients/oberalp.avif', alt: 'Oberalp Group' },
    { src: '/frontend_assets/media/images/main-clients/pharmanutra.avif', alt: 'Pharmanutra' },
    { src: '/frontend_assets/media/images/main-clients/holyart.webp', alt: 'Holyart', noFilter: true, customHeight: 90 },
    { src: '/frontend_assets/media/images/main-clients/oltresole.webp', alt: 'Oltresole', noFilter: true, customHeight: 45 },
  ];
  const clients = cms.items || defaultClients;
  const title = (useCms && cms.title) || t.clients.title;
  return (
    <section className="clients-refined py-5" data-testid="clients-section">
      <div className="container">
        <div className="clients-refined__header">
          <span className="clients-refined__line"></span>
          <h3 className="clients-refined__label">{title}</h3>
          <span className="clients-refined__line"></span>
        </div>
      </div>
      <div className="clients-refined__marquee">
        <div className="clients-refined__track">
          {[...clients,...clients,...clients].map((c,i)=>(
            <div key={i} className="clients-refined__item">
              <img src={c.src} alt={c.alt} loading="lazy" width="217" height="140" style={{ ...(c.noFilter ? { filter: 'none' } : {}), ...(c.customHeight ? { height: c.customHeight } : {}) }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Why Bonsai (unchanged structure, just translated header) ───── */
function WhyBonsaiSection() {
  const { t } = useLang();
  const hexRows = [
    [{title:'CSV/Excel',type:'text',label:t.whybonsai.labels.csvExcel,icon:'bi-file-earmark-spreadsheet'},{title:'Shopify',type:'img',src:'/frontend_assets/media/images/marketplaces/Shopify_logo.svg'},{title:'Magento',type:'img',src:'/frontend_assets/media/images/marketplaces/magento.svg'}],
    [{title:'Dynamics 365',type:'img',src:'/frontend_assets/media/images/marketplaces/dynamics_365.svg'},{title:'Zucchetti',type:'img',src:'/frontend_assets/media/images/marketplaces/zucchetti.webp'},{title:'ERP',type:'img',src:'/frontend_assets/media/images/marketplaces/erp.webp'}],
    [{title:'SAP',type:'img',src:'/frontend_assets/media/images/marketplaces/sap_logo.svg'},{title:'Sistemi',type:'img',src:'/frontend_assets/media/images/marketplaces/sistemi.webp'},{title:'API Custom',type:'text',label:t.whybonsai.labels.apiCustom,icon:'bi-code-slash'}],
    [{title:'WMS',type:'text',label:t.whybonsai.labels.wms,icon:'bi-boxes'},{title:'Altri',type:'text',label:t.whybonsai.labels.altri,icon:'bi-three-dots'}],
  ];
  const features = [{cls:'automation',icon:'bi-gear-fill',label:t.whybonsai.features.automation},{cls:'marketplace',icon:'bi-shop-window',label:t.whybonsai.features.marketplace},{cls:'analytics',icon:'bi-graph-up',label:t.whybonsai.features.analytics},{cls:'warehouse',icon:'bi-box-seam',label:t.whybonsai.features.warehouse},{cls:'integration',icon:'bi-plug',label:t.whybonsai.features.integration},{cls:'operations',icon:'bi-clipboard-check',label:t.whybonsai.features.operations}];
  const outputs = [{type:'channel',icon:'bi-plus-circle-fill',label:t.whybonsai.outputs.multichannel},{type:'img',src:'/frontend_assets/media/images/marketplaces/amazon-logo.svg',alt:'Amazon'},{type:'img',src:'/frontend_assets/media/images/marketplaces/ebay-logo.svg',alt:'eBay'},{type:'img',src:'/frontend_assets/media/images/marketplaces/tiktok-shop-logo.svg',alt:'TikTok Shop'},{type:'img',src:'/frontend_assets/media/images/marketplaces/temu-logo.svg',alt:'Temu'},{type:'img',src:'/frontend_assets/media/images/marketplaces/manomano-logo.svg',alt:'ManoMano'},{type:'img',src:'/frontend_assets/media/images/magazines/huboo-logo.svg',alt:'Huboo'},{type:'img',src:'/frontend_assets/media/images/magazines/hyve-logo.svg',alt:'Hyve'}];
  const couriers = [{src:'/frontend_assets/media/images/couriers/brt.svg',title:'BRT'},{src:'/frontend_assets/media/images/couriers/gls.svg',title:'GLS'},{src:'/frontend_assets/media/images/couriers/dhl.svg',title:'DHL'},{src:'/frontend_assets/media/images/couriers/ups.svg',title:'UPS'},{src:'/frontend_assets/media/images/couriers/tnt.svg',title:'TNT'},{src:'/frontend_assets/media/images/couriers/poste-italiane.svg',title:'Poste Italiane'}];
  return (
    <section id="whyBonsai" className="why-bonsai" data-testid="why-bonsai-section">
      <div className="container-fluid py-6">
        <div className="why-bonsai__header pb-5">
          <p className="text-uppercase text-success fw-semibold mb-2 fs-5">{t.whybonsai.badge}</p>
          <h2 className="why-bonsai__title display-2">{t.whybonsai.title}</h2>
          <p className="why-bonsai__subtitle fs-5">{t.whybonsai.subtitle}</p>
        </div>
        <div className="why-bonsai__container">
          <div className="why-bonsai__hex-grid">{hexRows.map((row,ri)=>(<div key={ri} className="why-bonsai__hex-row">{row.map((item,ci)=>(<div key={ci} className="why-bonsai__hex-item" title={item.title}>{item.type==='img'?<img src={item.src} alt={item.title} loading="lazy" width="68" height="68" className="img-fluid"/>:<div className="d-flex flex-column justify-content-center align-items-center"><div className="text-black fs-10 fw-bold">{item.label}</div><div><i className={`bi ${item.icon}`}></i></div></div>}</div>))}</div>))}</div>
          <div className="why-bonsai__connector why-bonsai__connector--branching"><svg viewBox="0 0 100 200" preserveAspectRatio="none"><defs><linearGradient id="gl" x1="0%" y1="50%" x2="100%" y2="50%"><stop offset="0%" style={{stopColor:'rgba(255,255,255,0.3)'}}/><stop offset="50%" style={{stopColor:'#11b767'}}/><stop offset="100%" style={{stopColor:'#11b767'}}/></linearGradient></defs>{['M0,20 Q50,20 100,100','M0,50 Q50,50 100,100','M0,150 Q50,150 100,100','M0,180 Q50,180 100,100'].map((d,i)=>(<React.Fragment key={i}><path className="why-bonsai__branch-line" d={d} stroke="url(#gl)"/><path className={`why-bonsai__glow-line why-bonsai__glow-line--${i+1}`} d={d}/></React.Fragment>))}</svg></div>
          <div className="why-bonsai__hub"><img src="/frontend_assets/media/logos/logo-monogram.svg" alt="Bonsai" className="why-bonsai__hub-logo" width="80" height="80"/><p className="why-bonsai__hub-name">bonsai</p></div>
          <div className="why-bonsai__connector why-bonsai__connector--branching"><svg viewBox="0 0 100 280" preserveAspectRatio="none"><defs><linearGradient id="gr" x1="0%" y1="50%" x2="100%" y2="50%"><stop offset="0%" style={{stopColor:'#11b767'}}/><stop offset="100%" style={{stopColor:'rgba(255,255,255,0.3)'}}/></linearGradient></defs>{[20,60,100,180,220,260].map((y,i)=>(<React.Fragment key={i}><path className="why-bonsai__branch-line" d={`M0,140 Q50,140 100,${y}`} stroke="url(#gr)"/><path className={`why-bonsai__glow-line why-bonsai__glow-line--${i+1}`} d={`M0,140 Q50,140 100,${y}`}/></React.Fragment>))}</svg></div>
          <div className="why-bonsai__features">{features.map((f,i)=>(<div key={i} className={`why-bonsai__feature why-bonsai__feature--${f.cls}`} data-feature-type={f.cls}><i className={`bi ${f.icon}`}></i>{f.label}</div>))}</div>
          <div className="why-bonsai__connector why-bonsai__connector--branching"><svg viewBox="0 0 100 280" preserveAspectRatio="none"><defs><linearGradient id="go" x1="0%" y1="50%" x2="100%" y2="50%"><stop offset="0%" style={{stopColor:'#11b767'}}/><stop offset="100%" style={{stopColor:'rgba(255,255,255,0.5)'}}/></linearGradient></defs>{[[20,40],[60,80],[100,120],[180,170],[220,200],[260,240]].map(([y1,y2],i)=>(<React.Fragment key={i}><path className="why-bonsai__branch-line" d={`M0,${y1} Q50,${y1} 100,${y2}`} stroke="url(#go)"/><path className={`why-bonsai__glow-line why-bonsai__glow-line--${i+1}`} d={`M0,${y1} Q50,${y1} 100,${y2}`}/></React.Fragment>))}</svg></div>
          <div className="why-bonsai__outputs">{outputs.map((o,i)=>o.type==='channel'?(<div key={i} className="why-bonsai__output why-bonsai__output--channels"><i className={`bi ${o.icon}`}></i><span>{o.label}</span></div>):(<div key={i} className="why-bonsai__output" data-related="marketplace"><img src={o.src} alt={o.alt} width="100" height="28" loading="lazy"/></div>))}</div>
          <section className="couriers-section"><div className="py-4"><div className="couriers-section__container">
            <div className="couriers-section__feature why-bonsai__feature why-bonsai__feature--shipping fade-up-mobile"><i className="bi bi-truck"></i>{t.whybonsai.features.shipping}</div>
            <div className="couriers-section__connector"><svg viewBox="0 0 100 280" preserveAspectRatio="none"><defs><linearGradient id="gc" x1="0%" y1="50%" x2="100%" y2="50%"><stop offset="0%" style={{stopColor:'#11b767'}}/><stop offset="55%" style={{stopColor:'#11b767'}}/><stop offset="100%" style={{stopColor:'rgba(255,255,255,0.25)'}}/></linearGradient></defs>{[20,70,120,170,220,270].map((y,i)=>(<React.Fragment key={i}><path className="couriers-section__branch-line" d={`M0,140 Q48,140 100,${y}`} stroke="url(#gc)"/><path className={`couriers-section__glow-line couriers-section__glow-line--${i+1}`} d={`M0,140 Q48,140 100,${y}`}/></React.Fragment>))}</svg></div>
            <div className="couriers-section__outputs">{couriers.map((c,i)=>(<div key={i} className="couriers-section__item fade-up-mobile" title={c.title}><img src={c.src} alt={c.title} loading="lazy" width="80" height="40"/></div>))}</div>
          </div></div></section>
        </div>
      </div>
    </section>
  );
}

/* ───── Modules Section (NEW from v2) ───── */
function ModulesSection() {
  const { t, lang } = useLang();
  const cms = useSiteContent('modules_section');
  const useCms = lang === 'it';
  const defaultModules = isEn => isEn ? [
    {id:'Listing & Catalogue Management',icon:'bi-list-check',desc:'A single catalogue for every marketplace',badges:['Amazon','eBay','Temu','Shopify','TikTok Shop'],features:['Multi-marketplace publishing','Multi-language editor','Variants and bundles','SKU/ASIN mapping','Bulk import/export']},
    {id:'Inventory, Stock & Pricing',icon:'bi-box-seam',desc:'Stock and prices synced in real time',badges:['Multi-warehouse','FBA','3PL','Repricing'],features:['Real-time stock sync','Multi-warehouse','Dynamic repricing','Safety stock and alerts','Multi-currency price lists']},
    {id:'Administration & Accounting',icon:'bi-receipt',desc:'EU e-invoicing and automated reconciliation',badges:['SDI','Aruba','Zucchetti','SAP'],features:['Automatic XML invoices','Payment reconciliation','Credit notes and returns','Accountant export','VAT reports']},
    {id:'Product & Competitor Tracking',icon:'bi-binoculars',desc:'Real-time competitor prices and positioning',badges:['Amazon','eBay','ManoMano','Temu'],features:['Automatic price tracking','Buy Box analysis','Change alerts','Market trends','Dynamic pricing']},
    {id:'Smart Logistics',icon:'bi-truck',desc:'Multi-courier routing and optimised shipping',badges:['BRT','GLS','DHL','UPS','TNT','Poste'],features:['Smart routing','Bulk labels','Centralised tracking','Courier SLA monitoring','Automated returns']},
    {id:'BI Analytics',icon:'bi-bar-chart-line',desc:'KPI dashboards for data-driven decisions',badges:['Sales','Margins','Inventory','Performance'],features:['Custom KPI dashboards','Per-channel reports','Per-product margins','Anomaly alerts','Forecasting']},
    {id:'Marketing & Advertising',icon:'bi-megaphone',desc:'Ad campaigns on Amazon and marketplaces with AI',badges:['Amazon Ads','Google Ads','Meta Ads','TACOS','ROAS'],features:['AI-powered campaign creation','Cross-channel attribution','ROAS optimisation','Sponsored Products/Brands','Ad spend allocation'],preview:true},
    {id:'Campaign Manager',icon:'bi-bullseye',desc:'Create, plan and monitor all campaigns from one hub',badges:['Multi-channel','Scheduling','Reporting','A/B testing'],features:['Unified campaign editor','Budget planning','Keyword and audience targeting','A/B testing','Consolidated reporting'],preview:true},
  ] : [
    {id:'Listing & Catalogue Management',icon:'bi-list-check',desc:'Catalogo unico per tutti i marketplace',badges:['Amazon','eBay','Temu','Shopify','TikTok Shop'],features:['Pubblicazione multi-marketplace','Editor multi-lingua','Varianti e bundle','Mapping SKU/ASIN','Import/export massivo']},
    {id:'Inventario, Giacenza e Prezzi',icon:'bi-box-seam',desc:'Stock e prezzi sincronizzati in real-time',badges:['Multi-magazzino','FBA','3PL','Repricing'],features:['Stock sync real-time','Multi-magazzino','Repricing dinamico','Safety stock e alert','Listini multi-valuta']},
    {id:'Amministrazione e Contabilità',icon:'bi-receipt',desc:'Fatturazione SDI e riconciliazione automatica',badges:['SDI','Aruba','Zucchetti','SAP'],features:['Fatture XML automatiche','Riconciliazione pagamenti','Note credito e resi','Export commercialista','Report IVA']},
    {id:'Tracking Prodotto & Competitor',icon:'bi-binoculars',desc:'Prezzi e posizionamento competitor real-time',badges:['Amazon','eBay','ManoMano','Temu'],features:['Price tracking automatico','Analisi Buy Box','Alert variazioni','Trend di mercato','Pricing dinamico']},
    {id:'Logistica Intelligente',icon:'bi-truck',desc:'Routing multi-corriere e spedizioni ottimizzate',badges:['BRT','GLS','DHL','UPS','TNT','Poste'],features:['Routing intelligente','Etichette bulk','Tracking centralizzato','Monitor SLA corrieri','Resi automatizzati']},
    {id:'BI Analytics',icon:'bi-bar-chart-line',desc:'Dashboard KPI per decisioni data-driven',badges:['Vendite','Margini','Inventario','Performance'],features:['Dashboard KPI custom','Report per canale','Margini per prodotto','Alert anomalie','Forecasting']},
    {id:'Marketing & Advertising',icon:'bi-megaphone',desc:'Campagne Amazon Ads, Amazon DSP e marketplace con AI',badges:['Amazon Ads','Google Ads','Meta Ads','TACOS','ROAS'],features:['Creazione campagne con AI','Attribuzione cross-channel','Ottimizzazione ROAS','Sponsored Products/Brands','Allocazione ad spend'],preview:true},
    {id:'Campaign Manager',icon:'bi-bullseye',desc:'Crea, pianifica e monitora tutte le campagne',badges:['Multi-canale','Programmazione','Reporting','A/B testing'],features:['Editor campagne unificato','Pianificazione budget','Targeting keyword e audience','Test A/B','Reporting consolidato'],preview:true},
  ];
  // In EN, prefer translated defaults over IT-only CMS items.
  const modules = useCms ? (cms.items || defaultModules(false)) : defaultModules(true);
  const badge = (useCms && cms.badge) || t.modules.badge;
  const modTitle = (useCms && cms.title) || t.modules.title;
  const subtitle = (useCms && cms.subtitle) || t.modules.subtitle;
  return (
    <section className="py-6" data-testid="modules-section">
      <div className="container py-4">
        <div className="text-center mb-5">
          <p className="text-uppercase text-success fw-semibold mb-2">{badge}</p>
          <h2 className="fw-bold">{modTitle}</h2>
          <p className="text-white-50">{subtitle}</p>
        </div>
        <div className="row g-4" id="modulesAccordion">
          {modules.map((m,i)=>{const colId=`module${i}`;return(
            <div key={i} className="col-lg-6 fade-up-pillars reveal" style={{transitionDelay:`${i*0.08}s`}}>
              <div className="module-card rounded-4 bg-dark border border-secondary overflow-hidden">
                <div className="module-card__header p-4 d-flex justify-content-between align-items-center" role="button" data-bs-toggle="collapse" data-bs-target={`#${colId}`}>
                  <div className="d-flex align-items-center gap-3">
                    <div className="module-card__icon bg-success bg-opacity-10 rounded-3 p-3"><i className={`bi ${m.icon} text-success fs-4`}></i></div>
                    <div><h3 className="fw-semibold fs-5 text-white mb-1">{m.id}{m.preview && (<span className="badge ms-2" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: '0.55em', fontWeight: 700, letterSpacing: '0.05em', verticalAlign: 'middle' }}>PREVIEW</span>)}</h3><p className="small text-white-50 mb-0">{m.desc}</p></div>
                  </div>
                  <i className="bi bi-chevron-down text-success fs-5 module-card__chevron"></i>
                </div>
                <div className="collapse" id={colId} data-bs-parent="#modulesAccordion">
                  <div className="module-card__body p-4 pt-0 border-top border-secondary">
                    <div className="row g-4">
                      <div className="col-md-5">
                        <p className="text-uppercase text-success fw-semibold small mb-2">{t.modules.channelsLabel}</p>
                        <div className="d-flex flex-wrap gap-2">{m.badges.map((b,j)=>(<span key={j} className="badge module-badge">{b}</span>))}</div>
                      </div>
                      <div className="col-md-7">
                        <p className="text-uppercase text-success fw-semibold small mb-2">{t.modules.featuresLabel}</p>
                        <ul className="list-unstyled small text-white-50 mb-0">{m.features.map((f,j)=>(<li key={j} className="mb-1"><i className="bi bi-check2 text-success me-2"></i>{f}</li>))}</ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );})}
        </div>
      </div>
    </section>
  );
}

/* ───── Comparison Table (NEW from v2) ───── */
function ComparisonSection() {
  const { t, lang } = useLang();
  const cms = useSiteContent('comparison');
  const useCms = lang === 'it';
  const defaultRows = [
    {feat:'All-in-one',bonsai:'Ordini, stock, spedizioni, contabilità, analytics in un unico pannello',comp:'Strumenti frammentati. Servono 3-4 software diversi',icon:'bi-grid-fill'},
    {feat:'Integrazioni ERP custom',bonsai:'Esperti in SAP, Zucchetti, Sistemi, Dynamics 365. Veniamo dalla consulenza gestionale',comp:'Solo ERP standard. Nessuna esperienza su gestionali italiani',icon:'bi-database'},
    {feat:'Nato dalle esigenze reali',bonsai:'Software progettato con i seller, non per i seller. Fondatori dal mondo consulenza e-commerce',comp:'Sviluppo product-led. Lontani dalle operation quotidiane',icon:'bi-lightbulb'},
    {feat:'Budgeting cross-modulo',bonsai:'Pianificazione budget che si riversa su tutti i moduli: ordini, spedizioni, margini',comp:'Nessuna gestione budget. Devi usare Excel',icon:'bi-piggy-bank'},
    {feat:'Programmi in Preview',bonsai:'Accesso anticipato Amazon, Temu e Shein. Partner ufficiali con feature in anteprima',comp:'Nessun accesso privilegiato. Stesse API pubbliche di tutti',icon:'bi-stars'},
    {feat:'Supporto dedicato',bonsai:'Team italiano. Risposta entro 10 min. Account manager personale',comp:'Ticket in inglese. Tempi lunghi',icon:'bi-headset'},
  ];
  const rows = cms.rows || defaultRows;
  const badge = (useCms && cms.badge) || t.comparison.badge;
  const compTitle = (useCms && cms.title) || t.comparison.title;
  const subtitle = (useCms && cms.subtitle) || t.comparison.subtitle;
  const competitor = (useCms && cms.competitor_label) || t.comparison.competitor;
  return (
    <section className="py-6" data-testid="comparison-section">
      <div className="container py-4">
        <div className="text-center mb-5">
          <p className="text-uppercase text-success fw-semibold mb-2">{badge}</p>
          <h2 className="fw-bold">{compTitle}</h2>
          <p className="text-white-50">{subtitle}</p>
        </div>
        {/* Desktop */}
        <div className="table-responsive d-none d-lg-block">
          <table className="table table-dark align-middle mb-0 comparison-table">
            <thead><tr>
              <th className="bg-transparent border-secondary" style={{width:'22%'}}></th>
              <th className="border-secondary" style={{width:'44%',background:'rgba(0,188,212,0.08)'}}><img src="/frontend_assets/media/logos/logo-monogram.svg" alt="" width="24" height="24" className="me-2"/>Bonsai</th>
              <th className="bg-transparent text-white-50 border-secondary" style={{width:'34%'}}>{competitor}</th>
            </tr></thead>
            <tbody>{rows.map((r,i)=>(<tr key={i}>
              <th scope="row" className="fw-semibold border-secondary"><i className={`bi ${r.icon} me-2`} style={{color:'var(--bs-light-blue)'}}></i>{r.feat}</th>
              <td className="border-secondary" style={{background:'rgba(0,188,212,0.04)'}}><i className="bi bi-check-circle-fill me-2" style={{color:'var(--bs-light-blue)'}}></i><span className="text-white">{r.bonsai}</span></td>
              <td className="border-secondary"><i className="bi bi-x-circle text-white-50 me-2" style={{opacity:0.4}}></i><span className="text-white-50">{r.comp}</span></td>
            </tr>))}</tbody>
          </table>
        </div>
        {/* Mobile */}
        <div className="d-lg-none">
          <div className="accordion comparison-accordion" id="comparisonAccordion">
            {rows.map((r,i)=>(<div key={i} className="accordion-item bg-dark border-secondary">
              <h3 className="accordion-header"><button className="accordion-button collapsed bg-dark text-white" type="button" data-bs-toggle="collapse" data-bs-target={`#comp${i}`}><i className={`bi ${r.icon} text-success me-2`}></i>{r.feat}</button></h3>
              <div id={`comp${i}`} className="accordion-collapse collapse" data-bs-parent="#comparisonAccordion"><div className="accordion-body">
                <div className="mb-3 p-3 rounded bg-success bg-opacity-10 border border-success border-opacity-25"><div className="d-flex align-items-center mb-2"><img src="/frontend_assets/media/logos/logo-monogram.svg" alt="" width="20" height="20" className="me-2"/><span className="fw-semibold text-success">Bonsai</span></div><p className="small text-white mb-0">{r.bonsai}</p></div>
                <div className="p-3 rounded competitor-box"><span className="fw-semibold competitor-label small">{competitor}</span><p className="small competitor-text mb-0 mt-1">{r.comp}</p></div>
              </div></div>
            </div>))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Press Section (NEW from v2) ───── */
function PressSection() {
  const { t } = useLang();
  return (
    <section className="py-6" data-testid="press-section" style={{position:'relative',overflow:'hidden'}}>
      <div className="tech-orb" style={{width:400,height:400,bottom:'-200px',left:'50%',marginLeft:'-200px',background:'rgba(0,188,212,0.1)'}}></div>
      <div className="container py-4"><div className="text-center mb-4"><p className="text-uppercase text-success fw-semibold mb-2 fs-5">{t.press.badge}</p></div></div>
      <div className="press-section py-5"><div className="container">
        <div className="d-flex flex-wrap justify-content-center align-items-center gap-5">
          <a href="https://lab24.ilsole24ore.com/leader-crescita/2023" target="_blank" rel="noopener noreferrer" className="text-decoration-none">
            <div className="press-badge"><img src="/frontend_assets/media/images/magazines/il_sole_24_ore_logo.svg" alt="Il Sole 24 Ore" loading="lazy" width="170" height="30" className="press-badge__logo"/><img src="/frontend_assets/media/images/magazines/statista_logo_1.svg" alt="Statista" loading="lazy" width="120" height="30" className="press-badge__statista"/><div className="press-badge__text"><span className="press-badge__title">LEADER</span><span className="press-badge__subtitle">DELLA</span><span className="press-badge__title">CRESCITA</span><span className="press-badge__year">2023</span></div></div>
          </a>
          <a href="https://www.ft.com/ft1000-2024" target="_blank" rel="noopener noreferrer" className="text-decoration-none">
            <div className="d-flex align-items-center gap-3"><div className="press-ft-logo"><span className="press-ft-logo__ft">FT</span><span className="press-ft-logo__label">FINANCIAL<br/>TIMES</span></div><div className="press-ft-info"><span className="press-ft-info__number">1000</span><span className="press-ft-info__text">Europe's Fastest<br/>Growing Companies</span><span className="press-ft-info__year">2022</span></div></div>
          </a>
          <a href="https://forbes.it/2024/11/04/cosi-questa-startup-aiuta-le-aziende-a-digitalizzare-la-supply-chain" target="_blank" rel="noopener noreferrer"><img src="/frontend_assets/media/images/magazines/forbes_logo_1.svg" alt="Forbes" style={{height:50,filter:'brightness(0) invert(1)',opacity:0.9}} loading="lazy"/></a>
          <a href="https://tinnovamag.com/publiredazionali/bonsai-growth-il-futuro-della-crescita-aziendale/" target="_blank" rel="noopener noreferrer"><img src="/frontend_assets/media/images/magazines/tinnovamag_logo.png" alt="Tecnologia & Innovazione" style={{height:50,filter:'brightness(0) invert(1)',opacity:0.9}} loading="lazy"/></a>
          <a href="https://www.g2.com/sellers/bonsai-software?source=search#reviews" target="_blank" rel="noopener noreferrer" aria-label="Recensioni Bonsai su G2" data-testid="press-g2"><img src="/frontend_assets/media/images/magazines/g2_logo.webp" alt="G2 Reviews" width="50" height="51" style={{height:50,width:'auto',filter:'brightness(0) invert(1)',opacity:0.9}} loading="lazy"/></a>
          <img src="/frontend_assets/media/images/magazines/trustpilot_logo.webp" alt="Trustpilot" width="180" height="44" style={{height:38,width:'auto',filter:'brightness(0) invert(1)',opacity:0.9}} loading="lazy" data-testid="press-trustpilot"/>
        </div>
      </div></div>
    </section>
  );
}

/* ───── FAQ ───── */
function FaqSection() {
  const { t, lang } = useLang();
  const cms = useSiteContent('faq');
  const useCms = lang === 'it';
  const defaultFaqs = [
    {q:'Quali marketplace supporta Bonsai?',a:'Bonsai supporta Amazon, Temu, ManoMano, TikTok Shop (B2B e B2C), eBay e si integra con i principali corrieri nazionali e internazionali come UPS, DHL, GLS e BRT.'},
    {q:"Quanto tempo richiede l'onboarding?",a:"L'attivazione tecnica e il go-live sul primo marketplace avvengono in poche ore. Il nostro team ti affianca passo passo su configurazione, migrazione dati e formazione, con supporto continuo dedicato."},
    {q:'Bonsai è conforme al GDPR?',a:'Sì, Bonsai è pienamente conforme al GDPR con SSO, 2FA, audit trail su azioni operative e backup continuo.'},
    {q:'Che tipo di supporto offrite?',a:'Supporto 7/7 h24 via chat tecnica con risposta entro 10 minuti, più un team dedicato per il go-live e account manager per i clienti enterprise.'},
  ];
  const faqs = cms.items || defaultFaqs;
  const badge = (useCms && cms.badge) || t.faq.badge;
  const faqTitle = (useCms && cms.title) || t.faq.title;
  const subtitle = (useCms && cms.subtitle) || t.faq.subtitle;
  return (
    <section className="py-6" data-testid="faq-section">
      <div className="container py-4">
        <div className="text-center mb-5">
          <p className="text-uppercase text-success fw-semibold mb-2">{badge}</p>
          <h2 className="fw-bold">{faqTitle}</h2>
          <p>{subtitle}</p>
        </div>
        <div className="row justify-content-center"><div className="col-lg-8"><div className="support-faq"><div className="accordion" id="faqAccordion">
          {faqs.map((f,i)=>(<div key={i} className="accordion-item"><h3 className="accordion-header"><button className={`accordion-button${i?' collapsed':''}`} type="button" data-bs-toggle="collapse" data-bs-target={`#faq${i}`}>{f.q}</button></h3><div id={`faq${i}`} className={`accordion-collapse collapse${i===0?' show':''}`} data-bs-parent="#faqAccordion"><div className="accordion-body">{f.a}</div></div></div>))}
        </div></div></div></div>
        <div className="text-center mt-4">
          <Link to="/faq" className="btn btn-outline-light px-5 py-2" data-testid="faq-discover-all"><i className="bi bi-arrow-right me-2"></i>{t.faq.discoverAll}</Link>
        </div>
      </div>
    </section>
  );
}

/* ───── Footer ───── */
function FooterLatestPosts() {
  const [posts, setPosts] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/blog?limit=3')
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data.items || []);
        setPosts(items.filter(p => p.published !== false).slice(0, 3));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (posts.length === 0) return null;
  return (
    <ul className="list-unstyled footer-latest-posts mt-2" data-testid="footer-latest-posts">
      {posts.map((p, i) => (
        <li key={p.slug || i} className="mb-2">
          <Link to={`/blog/${p.slug}`} className="footer-text-muted small d-block text-truncate" style={{ maxWidth: '100%' }} title={p.title}>
            <i className="bi bi-arrow-right-short me-1"></i>{p.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Footer() {
  const { t, lang, setLang } = useLang();
  const pages = [{name:t.common.home,path:'/'},{name:t.nav.prezzi,path:'/prezzi'},{name:t.nav.moduli,path:'/moduli'},{name:t.nav.onboarding,path:'/onboarding'},{name:t.nav.integrazioni,path:'/integrazioni'},{name:'Clienti',path:'/clienti'},{name:'FAQ',path:'/faq'},{name:t.nav.supporto,path:'/supporto'},{name:t.nav.blog,path:'/blog'},{name:t.common.contatti,path:'/contatti'}];
  return (
    <footer className="footer-main text-light pt-5 pb-4" data-testid="footer">
      <div className="container">
        <div className="row g-4 mb-5">
          <div className="col-lg-3 col-md-6">
            <h2 className="footer-title mb-4 fs-5">{t.footer.contact}</h2>
            <ul className="list-unstyled footer-contact">
              <li className="mb-2"><a href="mailto:info@bonsaigrowth.it"><i className="bi bi-envelope me-2"></i>Info@bonsaigrowth.it</a></li>
              <li className="mb-3"><a href="tel:+393282753183"><i className="bi bi-telephone me-2"></i>Tel: +39 328 2753183</a></li>
              <li className="mb-3"><p className="mb-1 text-light"><strong>{t.footer.sedeLegale}</strong></p><address className="footer-text-muted small mb-0">Via Francesco Ferrucci 100,<br/>Prato, PO, 59100, Italia</address></li>
              <li><p className="mb-1 text-light"><strong>{t.footer.sedeOperativa}</strong></p><address className="footer-text-muted small mb-0">Via Giacinto Fabbroni 11,<br/>Prato, PO, 59100, Italia</address></li>
            </ul>
          </div>
          <div className="col-lg-3 col-md-6">
            <h2 className="footer-title mb-4 fs-5">{t.footer.follow}</h2>
            <nav className="footer-social d-flex gap-3 mb-4" aria-label="Social media">
              <a href="https://www.linkedin.com/company/bonsaisolutions" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="fs-5"><i className="bi bi-linkedin"></i></a><a href="https://twitter.com/bonsaisolutions" target="_blank" rel="noopener noreferrer" aria-label="X / Twitter" className="fs-5"><i className="bi bi-twitter-x"></i></a><a href="https://www.instagram.com/bonsaisolutions" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="fs-5"><i className="bi bi-instagram"></i></a>
            </nav>
            <h2 className="mb-3 fs-5"><Link to="/blog" className="footer-blog-link">{t.footer.blog}</Link></h2>
            <Link to="/blog" className="footer-text-muted small">{t.footer.discover}</Link>
            <FooterLatestPosts />
          </div>
          <div className="col-lg-3 col-md-6">
            <h2 className="footer-title mb-4 fs-5">{t.footer.pages}</h2>
            <nav><ul className="list-unstyled footer-links">{pages.map((p,i)=>(<li key={i} className="mb-2"><Link to={p.path}>{p.name}</Link></li>))}</ul></nav>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="mb-4">
              <select className="form-select form-select-sm footer-lang-select" style={{width:'auto'}} value={lang} onChange={e=>setLang(e.target.value)} aria-label="Seleziona lingua" data-testid="lang-switcher">
                <option value="it">IT</option>
                <option value="en">EN</option>
              </select>
            </div>
            <ul className="list-unstyled footer-links mb-4">
              <li className="mb-2"><Link to="/termini" className="text-decoration-underline">{t.footer.terms}</Link></li>
              <li className="mb-2"><Link to="/privacy" className="text-decoration-underline">{t.footer.privacy}</Link></li>
              <li className="mb-2"><Link to="/sicurezza-dati" className="text-decoration-underline" data-testid="footer-security">Sicurezza e protezione dei dati</Link></li>
              <li className="mb-2"><Link to="/docs/claude-mcp-connector" className="text-decoration-underline" data-testid="footer-mcp-docs">Claude MCP Connector</Link></li>
              <li className="mb-2"><Link to="/docs/chatgpt-mcp-connector" className="text-decoration-underline" data-testid="footer-chatgpt-docs">ChatGPT MCP Connector</Link></li>
              <li className="mb-2"><Link to="/support/mcp-connector" className="text-decoration-underline" data-testid="footer-mcp-support">MCP Support</Link></li>
              <li className="mb-2"><Link to="/terms/mcp-connector" className="text-decoration-underline" data-testid="footer-mcp-terms">MCP Terms</Link></li>
              <li className="mb-2"><Link to="/privacy-mcp-connector" className="text-decoration-underline" data-testid="footer-mcp-privacy">MCP Privacy Policy</Link></li>
            </ul>
            <Link to="/contatti" className="btn footer-cta-btn px-4" data-testid="footer-cta">{t.footer.cta}</Link>
          </div>
        </div>
        <div className="footer-compliance pt-4"><h3 className="mb-3 fs-6">{t.footer.compliance}</h3>
          <p className="small mb-2 footer-text-muted">{t.footer.complianceLine1}</p>
          <p className="small mb-0 footer-text-muted">{t.footer.complianceLine2}</p>
        </div>
        <div className="footer-review-badges pt-4 mt-4" data-testid="footer-review-badges">
          <p className="small mb-3 footer-text-muted text-center text-uppercase" style={{ letterSpacing: '0.08em', fontWeight: 600 }}>Recensioni verificate</p>
          <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 mb-4">
            <a href="https://www.capterra.com/p/10043476/Bonsai/reviews/" target="_blank" rel="noopener noreferrer" aria-label="Recensioni Bonsai su Capterra">
              <img src="https://brand-assets.capterra.com/badge/3806a48a-1dee-4fdd-ba20-425ee2d5baee.svg" alt="Capterra Reviews" loading="lazy" width="160" height="76" style={{ height: 76, width: 160, objectFit: 'contain' }} />
            </a>
            <a href="https://www.getapp.com/all-software/a/bonsai-1/" target="_blank" rel="noopener noreferrer" aria-label="Recensioni Bonsai su GetApp">
              <img src="https://brand-assets.getapp.com/badge/39c319e3-1ea2-4d63-b9db-d5c19326950b.png" alt="GetApp Reviews" loading="lazy" style={{ maxHeight: 54 }} />
            </a>
            <a href="https://www.softwareadvice.com/product/550932-Bonsai/reviews/" target="_blank" rel="noopener noreferrer" aria-label="Recensioni Bonsai su Software Advice">
              <img src="https://brand-assets.softwareadvice.com/badge/f32d53a5-bb50-49fc-a911-1e5f337ba691.png" alt="Software Advice Reviews" loading="lazy" style={{ maxHeight: 54 }} />
            </a>
          </div>
          <p className="small mb-3 footer-text-muted text-center text-uppercase" style={{ letterSpacing: '0.08em', fontWeight: 600 }}>Partner ufficiali Amazon</p>
          <div className="d-flex flex-wrap justify-content-center align-items-center gap-4" data-testid="footer-partner-badges">
            <img src="https://customer-assets-lqy194kg.emergentagent.net/job_site-importer-6/artifacts/g2854e7l_amazon-ads-partner.webp" alt="Amazon Ads Verified Partner" loading="lazy" style={{ maxHeight: 72, background: '#fff', padding: '6px 10px', borderRadius: 8 }} />
            <img src="https://customer-assets-lqy194kg.emergentagent.net/job_site-importer-6/artifacts/vrggfkba_amazon-software-partners.webp" alt="Amazon Selling Partner Appstore — Software Partner" loading="lazy" style={{ maxHeight: 72, background: '#fff', padding: '6px 10px', borderRadius: 8 }} />
            <img src="https://customer-assets-lqy194kg.emergentagent.net/job_site-importer-6/artifacts/tl2q8156_amazon-appstore-badge-english-light.png" alt="Available at Amazon Appstore" loading="lazy" style={{ maxHeight: 72, background: '#fff', padding: '10px 14px', borderRadius: 8, maxWidth: 210, objectFit: 'contain' }} />
          </div>
        </div>
        <div className="footer-legal-info pt-4 mt-4 text-center" data-testid="footer-legal-info">
          <p className="small mb-1 text-light"><strong>Bonsai srls</strong></p>
          <p className="small mb-1 footer-text-muted">Partita IVA: 02533060972 · Codice Fiscale: 02533060972 · Vat Europeo: IT02533060972</p>
          <p className="small mb-0 footer-text-muted">Indirizzo: Via Francesco Ferrucci 100 — 59100 Prato (PO)</p>
        </div>
        <div className="footer-copyright pt-4 mt-4 text-center"><p className="small mb-0">{t.footer.copyright.replace('{year}', new Date().getFullYear())}</p></div>
      </div>
    </footer>
  );
}

/* ───── Homepage ───── */
function HomePage() {
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;
    // Counter Animation
    const counters = document.querySelectorAll('.counter-animate');
    counters.forEach(el=>{el._animated=false;});
    const cObs = new IntersectionObserver(entries=>{entries.forEach(entry=>{if(entry.isIntersecting&&!entry.target._animated){entry.target._animated=true;entry.target.classList.add('counter-visible');const el=entry.target;const target=parseFloat(el.dataset.target);const prefix=el.dataset.prefix||'';const suffix=el.dataset.suffix||'';const decimals=parseInt(el.dataset.decimals)||0;const start=performance.now();const tick=now=>{const p=Math.min((now-start)/2000,1);const e=1-Math.pow(1-p,3);const v=e*target;el.textContent=prefix+v.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g,'.')+suffix;if(p<1)requestAnimationFrame(tick);};requestAnimationFrame(tick);}});},{threshold:0.5});
    counters.forEach(el=>cObs.observe(el));
    // Fade-up
    document.querySelectorAll('.fade-up-pillars').forEach(el=>{el.style.opacity='0';el.style.transform='translateY(20px)';});
    const pObs=new IntersectionObserver(entries=>{entries.forEach(entry=>{if(entry.isIntersecting){entry.target.style.transition='opacity 0.8s ease, transform 0.8s ease';entry.target.style.opacity='1';entry.target.style.transform='translateY(0)';}});},{threshold:0.2,rootMargin:'-50px 0px'});
    document.querySelectorAll('.fade-up-pillars').forEach(el=>pObs.observe(el));
    // Why Bonsai
    const section=document.querySelector('.why-bonsai');
    if(section){const hexItems=section.querySelectorAll('.why-bonsai__hex-item');const hub=section.querySelector('.why-bonsai__hub');const features=section.querySelectorAll('.why-bonsai__feature');const outputs=section.querySelectorAll('.why-bonsai__output');const connectors=section.querySelectorAll('.why-bonsai__connector');
    const animate=()=>{hexItems.forEach((item,i)=>setTimeout(()=>item.classList.add('animate-in'),i*50));connectors.forEach((c,i)=>{setTimeout(()=>{c.style.opacity='1';c.querySelectorAll('.why-bonsai__branch-line').forEach((l,li)=>setTimeout(()=>l.classList.add('animate-in'),li*100));},300+i*200);});setTimeout(()=>hub&&hub.classList.add('animate-in'),400);features.forEach((f,i)=>setTimeout(()=>f.classList.add('animate-in'),700+i*80));outputs.forEach((o,i)=>setTimeout(()=>o.classList.add('animate-in'),1200+i*100));};
    const wObs=new IntersectionObserver(entries=>{entries.forEach(entry=>{if(entry.isIntersecting){animate();wObs.unobserve(entry.target);}});},{threshold:0.2});wObs.observe(section);
    features.forEach(f=>{f.addEventListener('mouseenter',()=>{const type=f.dataset.featureType;if(type)section.querySelectorAll(`[data-related="${type}"]`).forEach(o=>{o.style.transform='scale(1.1)';o.style.boxShadow='0 6px 20px rgba(0,0,0,0.15)';});});f.addEventListener('mouseleave',()=>{const type=f.dataset.featureType;if(type)section.querySelectorAll(`[data-related="${type}"]`).forEach(o=>{o.style.transform='';o.style.boxShadow='';});});});}
    // Couriers
    const cs=document.querySelector('.couriers-section');
    if(cs){const feat=cs.querySelector('.couriers-section__feature');const conns=cs.querySelectorAll('.couriers-section__connector');const items=cs.querySelectorAll('.couriers-section__item');const bLines=cs.querySelectorAll('.couriers-section__branch-line');const gLines=cs.querySelectorAll('.couriers-section__glow-line');
    const animC=()=>{setTimeout(()=>conns.forEach(c=>c.style.opacity='1'),250);setTimeout(()=>feat&&feat.classList.add('animate-in'),400);setTimeout(()=>{bLines.forEach((l,i)=>setTimeout(()=>l.classList.add('animate-in'),i*80));gLines.forEach((l,i)=>setTimeout(()=>l.classList.add('animate-in'),150+i*80));},550);items.forEach((item,i)=>setTimeout(()=>item.classList.add('animate-in'),750+i*90));};
    const cObs2=new IntersectionObserver(entries=>{entries.forEach(entry=>{if(entry.isIntersecting){animC();cObs2.unobserve(entry.target);}});},{threshold:0.2});cObs2.observe(cs);}
  }, []);
  return (
    <div data-testid="bonsai-homepage">
      <HeroSection />
      <hr className="animated-divider" />
      <ClientsMarquee />
      <hr className="animated-divider" />
      <PainPointsSection />
      <hr className="animated-divider" />
      <WhyModularSection />
      <hr className="animated-divider" />
      <MessaggiAI />
      <hr className="animated-divider" />
      <WhyBonsaiSection />
      <hr className="animated-divider" />
      <ModulesSection />
      <hr className="color-bar" />
      <ComparisonSection />
      <hr className="animated-divider" />
      <PressSection />
      <hr className="animated-divider" />
      <FaqSection />

      {/* ───── SEO content (visually hidden, readable by crawlers & screen readers) ───── */}
      <section
        id="seo-content"
        aria-label="Descrizione Bonsai"
        data-testid="seo-content-section"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        <h1>Bonsai — Software di Gestione Marketplace Multicanale</h1>
        <p>Bonsai è il software italiano per vendere su Amazon, Temu, ManoMano, TikTok Shop, eBay e Leroy Merlin da un'unica piattaforma. Gestisci ordini, magazzino, logistica e analytics in tempo reale con oltre 30 integrazioni native.</p>

        <h2>Marketplace supportati</h2>
        <p>Amazon, Temu, ManoMano, TikTok Shop, eBay, Leroy Merlin. Tutte le integrazioni sono native e mantenute aggiornate dal team Bonsai.</p>

        <h2>Integrazioni con corrieri e gestionali</h2>
        <p>Corrieri: BRT, GLS, DHL, UPS. ERP italiani e internazionali: SAP, Zucchetti, Microsoft Dynamics 365. Collegamento via API per qualsiasi altro gestionale.</p>

        <h2>Perché scegliere Bonsai</h2>
        <p>Go-live rapido con il Metodo Bonsai™: attivazione tecnica e primo marketplace operativo in poche ore, con team dedicato per formazione e ottimizzazione continua. Supporto 7/7 h24 con risposta entro 10 minuti via chat tecnica. Soluzione pensata per il mercato italiano con prezzi modulari per volume di ordini.</p>

        <h2>Domande frequenti</h2>
        <dl>
          <div><dt>Quali marketplace supporta Bonsai?</dt><dd>Amazon, Temu, ManoMano, TikTok Shop, eBay e Leroy Merlin. Integrazioni native sempre aggiornate.</dd></div>
          <div><dt>Bonsai si integra con SAP o Zucchetti?</dt><dd>Sì. Bonsai ha integrazioni native con SAP, Zucchetti e Microsoft Dynamics 365, oltre a BRT, GLS, DHL e UPS.</dd></div>
          <div><dt>Quanto tempo richiede l'onboarding?</dt><dd>Attivazione tecnica e go-live sul primo marketplace in poche ore, con team dedicato per configurazione, migrazione dati e formazione.</dd></div>
          <div><dt>Bonsai sincronizza il magazzino in tempo reale?</dt><dd>Sì. Prezzi, giacenze e catalogo vengono sincronizzati in tempo reale su tutti i marketplace connessi.</dd></div>
          <div><dt>In cosa si differenzia da Poleepo o bindCommerce?</dt><dd>Bonsai è focalizzato sul mercato italiano con integrazioni native per ERP e corrieri italiani, go-live rapido e supporto 7/7 h24 con risposta entro 10 minuti.</dd></div>
        </dl>
      </section>
    </div>
  );
}

/* ───── Layout & Router ───── */
/* ───── SEO wrapper per route ───── */
function PageWithSEO({ path, children }) { return <>{children}<SEO path={path} /></>; }

/* ───── Tech Particles ───── */
function TechParticles() {
  // Skip on mobile / coarse pointer devices to save TBT
  if (typeof window !== 'undefined') {
    if (window.innerWidth < 768) return null;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
  }
  return (
    <div className="tech-particles">
      {Array.from({length:8}).map((_,i)=>(
        <span key={i} style={{
          left: `${Math.random()*100}%`,
          width: `${2+Math.random()*4}px`, height: `${2+Math.random()*4}px`,
          animationDuration: `${8+Math.random()*12}s`,
          animationDelay: `${Math.random()*8}s`,
          opacity: 0.2+Math.random()*0.4,
        }}/>
      ))}
    </div>
  );
}

/* ───── Scroll Reveal Hook ───── */
function useScrollReveal() {
  const location = useLocation();
  useEffect(() => {
    const observed = new WeakSet();
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('revealed');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -10% 0px' });

    const SELECTOR = '.reveal, .reveal-left, .reveal-right, .reveal-scale';
    const observeAll = () => {
      document.querySelectorAll(SELECTOR).forEach(el => {
        if (observed.has(el) || el.classList.contains('revealed')) return;
        observed.add(el);
        // Safety net: if IntersectionObserver hasn't fired in 1.2s
        // (slow lazy chunk, off-viewport, parent display:none → visible later)
        // reveal anyway so the user never sees a blank section.
        setTimeout(() => {
          if (!el.classList.contains('revealed')) el.classList.add('revealed');
        }, 1200);
        obs.observe(el);
      });
    };

    observeAll();
    // Re-scan whenever React mounts new content (lazy chunks, data fetch, etc.)
    const mo = new MutationObserver(observeAll);
    mo.observe(document.body, { childList: true, subtree: true });

    // Mouse spotlight only on devices with a fine pointer (desktops/laptops)
    let spotlight = null;
    let onMove = null;
    const isFinePointer = window.matchMedia && window.matchMedia('(pointer: fine) and (min-width: 1024px)').matches;
    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isFinePointer && !reducedMotion) {
      spotlight = document.createElement('div');
      spotlight.className = 'mouse-spotlight';
      document.body.appendChild(spotlight);
      onMove = (e) => { spotlight.style.left = e.clientX + 'px'; spotlight.style.top = e.clientY + 'px'; };
      window.addEventListener('mousemove', onMove, { passive: true });
    }

    return () => {
      obs.disconnect();
      mo.disconnect();
      if (onMove) window.removeEventListener('mousemove', onMove);
      if (spotlight) spotlight.remove();
    };
  }, [location.pathname]); // re-init on every route change
}

function AppLayout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const { t } = useLang();
  useScrollReveal();
  if (isAdmin) return <div data-testid="bonsai-app"><ScrollToTop /><Suspense fallback={<div className="d-flex align-items-center justify-content-center" style={{minHeight:'100vh'}}><div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading...</span></div></div>}><Routes><Route path="/admin/*" element={<AdminPanel />} /></Routes></Suspense></div>;
  return (
    <div data-testid="bonsai-app">
      <a href="#main-content" className="skip-link visually-hidden-focusable">{t.a11y.skipToContent}</a>
      <ScrollToTop /><MetaPageViewTracker /><LegacyRedirect /><Navbar />
      <main id="main-content" className="general-wrapper default-margin-top" role="main">
        <TechParticles />
        <Suspense fallback={<div className="d-flex align-items-center justify-content-center" style={{minHeight:'60vh'}}><div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading...</span></div></div>}>
        <Routes>
          {[
            ['/', HomePage],
            ['/prezzi', PrezziPage],
            ['/onboarding', OnboardingPage],
            ['/amazon-dsp', AmazonDSPPage],
            ['/integrazioni', IntegrazioniPage],
            ['/moduli', ModuliPage],
            ['/faq', FaqFullPage],
            ['/supporto', SupportoPage],
            ['/blog', BlogPage],
            ['/clienti', ClientiPage],
            ['/contatti', ContattiPage],
            ['/termini', TermsPage],
            ['/privacy', PrivacyPage],
            ['/privacy-mcp-connector', McpConnectorPrivacyPage],
            ['/docs/claude-mcp-connector', McpConnectorDocsPage],
            ['/docs/chatgpt-mcp-connector', ChatgptMcpDocsPage],
            ['/support/mcp-connector', McpSupportPage],
            ['/terms/mcp-connector', McpTermsPage],
          ].flatMap(([itPath, Page]) => {
            const variants = ['', '/en', '/fr', '/de', '/es']; // '' = IT root
            return variants.map(prefix => {
              const fullPath = prefix === '' ? itPath : (itPath === '/' ? prefix : `${prefix}${itPath}`);
              return <Route key={fullPath} path={fullPath} element={<PageWithSEO path={itPath}><Page /></PageWithSEO>} />;
            });
          })}
          <Route path="/sicurezza-dati" element={<PageWithSEO path="/sicurezza-dati"><SecurityPage /></PageWithSEO>} />
          {(() => {
            // Localized slug for "integrazioni" per language (URLs Google will crawl)
            const INTEGR_SLUG = { '': 'integrazioni', '/en': 'integrations', '/fr': 'integrations', '/de': 'integrationen', '/es': 'integraciones' };
            return ['', '/en', '/fr', '/de', '/es'].flatMap(prefix => [
              // Localized integrations detail
              <Route key={`${prefix}/${INTEGR_SLUG[prefix]}/:slug`} path={`${prefix}/${INTEGR_SLUG[prefix]}/:slug`} element={<MarketplaceDetail />} />,
              // Localized integrations index (list page)
              prefix === '' ? null : (
                <Route key={`${prefix}/${INTEGR_SLUG[prefix]}`} path={`${prefix}/${INTEGR_SLUG[prefix]}`} element={<PageWithSEO path="/integrazioni"><IntegrazioniPage /></PageWithSEO>} />
              ),
              // Legacy /{lang}/integrazioni redirect handled by <Navigate>
              prefix === '' ? null : (
                <Route key={`legacy${prefix}/integrazioni/:slug`} path={`${prefix}/integrazioni/:slug`} element={<RedirectToLocalized prefix={prefix} slugWord={INTEGR_SLUG[prefix]} />} />
              ),
              prefix === '' ? null : (
                <Route key={`legacy${prefix}/integrazioni`} path={`${prefix}/integrazioni`} element={<RedirectToLocalized prefix={prefix} slugWord={INTEGR_SLUG[prefix]} />} />
              ),
              <Route key={`${prefix}/moduli/:slug`} path={`${prefix}/moduli/:slug`} element={<ModuleDetail />} />,
              <Route key={`${prefix}/risorse/:slug`} path={`${prefix}/risorse/:slug`} element={<RisorsePage />} />,
              <Route key={`${prefix}/blog/:slug`} path={`${prefix}/blog/:slug`} element={<BlogPost />} />,
              <Route key={`${prefix}/clienti/:slug`} path={`${prefix}/clienti/:slug`} element={<ClienteDetailPage />} />,
            ].filter(Boolean));
          })()}
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieBanner />
      <AIChatWidget />
    </div>
  );
}

function App() { return <BrowserRouter><LangProvider><AppLayout /></LangProvider></BrowserRouter>; }
export default App;
